import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { isPWAStandalone, formatCalories, formatMacronutrient, formatMicronutrient } from '../../lib/utils';
import { X, Clock, Users, ChefHat, Flame, Heart, Star, Plus, Check, Zap, ChevronDown, Package, ShoppingCart, Leaf, Utensils, Target, Coffee, Trash2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNoshStore } from '../../lib/store';
import { useLeftEdgeSwipeDisable } from '../../hooks/useLeftEdgeSwipeDisable';
import { Button } from './button';
import { Progress } from './progress';
import { supabase } from '../../lib/supabase';
import { DatabaseService, DailyNutritionSummary } from '../../lib/database';
import { calculateNutritionTargets, type DietaryPreference } from '../../lib/nutrition-calculator';
import { CachedImage } from './CachedImage';
import { getOptimizedImageUrl } from '../../lib/image-optimization';

// Mock recipe data - will be replaced with real data from API
const mockRecipe = {
  id: 'recipe-1',
  name: 'Mediterranean Quinoa Bowl',
  image: 'https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=400&h=400&fit=crop&crop=center',
  calories: 420,
  cookTime: 25,
  difficulty: 'Easy',
  servings: 2,
  rating: 4.8,
  description: 'A vibrant and nutritious Mediterranean-inspired quinoa bowl packed with fresh vegetables, protein, and bold flavors.',
  ingredients: [
    { name: 'Quinoa', amount: '1 cup', category: 'grains' },
    { name: 'Cherry tomatoes', amount: '1 cup', category: 'vegetables' },
    { name: 'Cucumber', amount: '1 medium', category: 'vegetables' },
    { name: 'Red onion', amount: '1/4 cup', category: 'vegetables' },
    { name: 'Feta cheese', amount: '1/2 cup', category: 'dairy' },
    { name: 'Olive oil', amount: '3 tbsp', category: 'oils' },
    { name: 'Lemon juice', amount: '2 tbsp', category: 'condiments' },
    { name: 'Fresh herbs', amount: '1/4 cup', category: 'herbs' }
  ],
  instructions: [
    { step: 1, instruction: 'Rinse quinoa thoroughly and cook according to package directions. Let cool slightly.' },
    { step: 2, instruction: 'While quinoa cooks, dice tomatoes, cucumber, and red onion into small pieces.' },
    { step: 3, instruction: 'In a large bowl, whisk together olive oil, lemon juice, salt, and pepper.' },
    { step: 4, instruction: 'Add cooked quinoa to the bowl and toss with dressing.' },
    { step: 5, instruction: 'Fold in diced vegetables and crumbled feta cheese.' },
    { step: 6, instruction: 'Garnish with fresh herbs and serve immediately or chill for later.' }
  ],
  nutrition: {
    calories: 420,
    protein: 18,
    carbs: 52,
    fat: 16,
    fiber: 8,
    sugar: 6,
    sodium: 380
  },
  tags: [] // Removed generic tags - nutrition facts are more useful
};

// REMOVED: Complex emoji mapping functions that caused memory leaks

// ENHANCED: Better ingredient matching with comprehensive emoji support
// Smart tag deduplication that handles semantic duplicates
const deduplicateSmartTags = (tags: string[]): string[] => {
  const dedupedTags: string[] = [];
  const seenTags = new Set<string>();
  
  // Spice level normalization map - prefer the formal contextual tags
  const spiceNormalization: { [key: string]: string } = {
    'no spice': 'No Spice',
    'none spice': 'No Spice',
    'mild spice': 'Mild Spice',
    'medium spice': 'Medium Spice',
    'spicy': 'Spicy'
  };
  
  // Difficulty normalization map
  const difficultyNormalization: { [key: string]: string } = {
    'easy': 'Easy',
    'medium': 'Medium', 
    'hard': 'Hard'
  };
  
  // Meal type normalization map
  const mealTypeNormalization: { [key: string]: string } = {
    'breakfast': 'Breakfast',
    'lunch': 'Lunch',
    'dinner': 'Dinner',
    'snack': 'Snack',
    'dessert': 'Dessert'
  };
  
  for (const tag of tags) {
    const tagLower = tag.toLowerCase().trim();
    
    // Normalize spice level tags
    const normalizedSpice = spiceNormalization[tagLower];
    if (normalizedSpice) {
      if (!seenTags.has(normalizedSpice.toLowerCase())) {
        dedupedTags.push(normalizedSpice);
        seenTags.add(normalizedSpice.toLowerCase());
      }
      continue;
    }
    
    // Normalize difficulty tags
    const normalizedDifficulty = difficultyNormalization[tagLower];
    if (normalizedDifficulty) {
      if (!seenTags.has(normalizedDifficulty.toLowerCase())) {
        dedupedTags.push(normalizedDifficulty);
        seenTags.add(normalizedDifficulty.toLowerCase());
      }
      continue;
    }
    
    // Normalize meal type tags
    const normalizedMealType = mealTypeNormalization[tagLower];
    if (normalizedMealType) {
      if (!seenTags.has(normalizedMealType.toLowerCase())) {
        dedupedTags.push(normalizedMealType);
        seenTags.add(normalizedMealType.toLowerCase());
      }
      continue;
    }
    
    // Regular deduplication for other tags
    if (!seenTags.has(tagLower)) {
      dedupedTags.push(tag);
      seenTags.add(tagLower);
    }
  }
  
  return dedupedTags;
};

const addEmojisToInstruction = (instruction: string, inventory: any[], isPhotoRecipe?: boolean, detectedIngredients?: string[]): JSX.Element => {
  // Create comprehensive ingredient-to-emoji map from user's inventory
  const inventoryEmojiMap: Record<string, string> = {};
  const fullIngredientNames: string[] = []; // Track full ingredient names for multi-word matching
  
  inventory.forEach(item => {
    if (item.name && item.emoji) {
      const itemName = item.name.toLowerCase();
      inventoryEmojiMap[itemName] = item.emoji;
      fullIngredientNames.push(itemName);
      
      // Add common variations for better matching
      if (itemName.includes(' ')) {
        // For "Red Onion" -> also match "onion"
        const words = itemName.split(' ');
        if (words.length === 2) {
          inventoryEmojiMap[words[1]] = item.emoji;
          // Also match plural forms
          if (!words[1].endsWith('s')) {
            inventoryEmojiMap[words[1] + 's'] = item.emoji;
          }
        }
      }
      
      // Add plural/singular variations
      if (!itemName.endsWith('s') && itemName.length > 3) {
        inventoryEmojiMap[itemName + 's'] = item.emoji;
      }
      if (itemName.endsWith('s') && itemName.length > 3) {
        inventoryEmojiMap[itemName.slice(0, -1)] = item.emoji;
      }
    }
  });
  
  // Enhanced fallback emojis for common cooking ingredients
  const basicEmojis: Record<string, string> = {
    'salt': '🧂', 'pepper': '⚫', 'oil': '🫒', 'olive': '🫒', 'butter': '🧈', 
    'garlic': '🧄', 'onion': '🧅', 'onions': '🧅', 'water': '💧', 'flour': '🌾', 
    'sugar': '🍯', 'vinegar': '🍶', 'lemon': '🍋', 'lime': '🟢',
    'tomato': '🍅', 'tomatoes': '🍅', 'basil': '🌿', 'herbs': '🌿',
    'pineapple': '🍍', 'pork': '🥩', 'chicken': '🐔', 'beef': '🥩',
    'cheese': '🧀', 'milk': '🥛', 'egg': '🥚', 'eggs': '🥚',
    // Add specific ingredients that are commonly used
    'miso': '🍱', 'miso paste': '🍱', 'white miso': '🍱', 'red miso': '🍱',
    'rice paper': '🍚', 'spring roll': '🍚', 'wrapper': '🍚',
    'cream': '🥛', 'thickened cream': '🥛', 'heavy cream': '🥛', 'whipping cream': '🥛'
  };

  // PHASE 1: Find and replace multi-word ingredients first (longest matches first)
  let processedInstruction = instruction;
  const matchedRanges: Array<{start: number, end: number, ingredient: string, emoji: string}> = [];
  
  // Sort ingredient names by length (longest first) to prevent shorter matches overriding longer ones
  const sortedIngredients = [...fullIngredientNames].sort((a, b) => b.length - a.length);
  
  sortedIngredients.forEach(ingredientName => {
    const regex = new RegExp(`\\b${ingredientName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'gi');
    let match;
    
    while ((match = regex.exec(processedInstruction)) !== null) {
      // Check if this range is already covered by a longer match
      const hasOverlap = matchedRanges.some(range => 
        (match!.index >= range.start && match!.index < range.end) ||
        (match!.index + match![0].length > range.start && match!.index + match![0].length <= range.end)
      );
      
      if (!hasOverlap) {
        matchedRanges.push({
          start: match.index,
          end: match.index + match[0].length,
          ingredient: match[0],
          emoji: inventoryEmojiMap[ingredientName]
        });
      }
    }
  });
  
  // Sort matches by start position for processing
  matchedRanges.sort((a, b) => a.start - b.start);
  
  // PHASE 2: Build the JSX with multi-word matches and individual word processing
  const result: JSX.Element[] = [];
  let lastIndex = 0;
  let elementKey = 0;
  
  matchedRanges.forEach(match => {
    // Add text before this match
    if (match.start > lastIndex) {
      const beforeText = processedInstruction.slice(lastIndex, match.start);
      result.push(...processWordsInText(beforeText, inventoryEmojiMap, basicEmojis, isPhotoRecipe, detectedIngredients, elementKey));
      elementKey += beforeText.split(/(\s+|\.|,|!|\?|;|:)/).length;
    }
    
    // Add the highlighted multi-word ingredient
    result.push(
      <span
        key={elementKey++}
        className="inline-flex items-center gap-1 bg-blue-50 dark:bg-blue-900/20 border-b-2 border-dashed border-blue-300 dark:border-blue-600 px-1 py-0.5 rounded-sm font-medium"
        title="From your inventory"
      >
        <span className="text-sm">{match.emoji}</span>
        <span>{match.ingredient}</span>
      </span>
    );
    
    lastIndex = match.end;
  });
  
  // Add remaining text after last match
  if (lastIndex < processedInstruction.length) {
    const remainingText = processedInstruction.slice(lastIndex);
    result.push(...processWordsInText(remainingText, inventoryEmojiMap, basicEmojis, isPhotoRecipe, detectedIngredients, elementKey));
  }
  
  return <>{result}</>;
};

// Helper function to process individual words in text sections
const processWordsInText = (
  text: string, 
  inventoryEmojiMap: Record<string, string>, 
  basicEmojis: Record<string, string>, 
  isPhotoRecipe?: boolean, 
  detectedIngredients?: string[],
  startKey: number = 0
): JSX.Element[] => {
  const words = text.split(/(\s+|\.|,|!|\?|;|:)/);
  
  return words.map((word, index) => {
    const cleanWord = word.toLowerCase().trim().replace(/[.,!?;:()"]/g, '');
    
    // Skip very short words or empty strings
    if (cleanWord.length < 3) {
      return <span key={startKey + index}>{word}</span>;
    }
    
    // For photo recipes, check if word matches any detected ingredients
    if (isPhotoRecipe && detectedIngredients) {
      const isDetectedIngredient = detectedIngredients.some(ingredient => {
        const ingredientLower = ingredient.toLowerCase();
        return ingredientLower.includes(cleanWord) || cleanWord.includes(ingredientLower);
      });
      
      if (isDetectedIngredient) {
        // Get emoji from inventory or basic emojis
        const emoji = inventoryEmojiMap[cleanWord] || basicEmojis[cleanWord] || '';
        return (
          <span
            key={startKey + index}
            className="inline-flex items-center gap-1 bg-gray-100 dark:bg-gray-700 border-b-2 border-dashed border-gray-400 dark:border-gray-500 px-1 py-0.5 rounded-sm font-medium"
            title="Photo detected ingredient"
          >
            {emoji && <span className="text-sm">{emoji}</span>}
            <span>{word.replace(/[.,!?;:()"]/g, '')}</span>
          </span>
        );
      }
    }
    
    // For non-photo recipes, use original logic
    if (!isPhotoRecipe) {
      // Check exact match in inventory first (highest priority)
      const inventoryEmoji = inventoryEmojiMap[cleanWord];
      if (inventoryEmoji) {
        return (
          <span
            key={startKey + index}
            className="inline-flex items-center gap-1 bg-blue-50 dark:bg-blue-900/20 border-b-2 border-dashed border-blue-300 dark:border-blue-600 px-1 py-0.5 rounded-sm font-medium"
            title="From your inventory"
          >
            <span className="text-sm">{inventoryEmoji}</span>
            <span>{word.replace(/[.,!?;:()"]/g, '')}</span>
          </span>
        );
      }
      
      // Check basic cooking ingredients
      const basicEmoji = basicEmojis[cleanWord];
      if (basicEmoji) {
        return (
          <span
            key={startKey + index}
            className="inline-flex items-center gap-1 bg-gray-50 dark:bg-gray-800/50 border-b border-dashed border-gray-300 dark:border-gray-600 px-1 py-0.5 rounded-sm"
            title="Common ingredient"
          >
            <span className="text-sm">{basicEmoji}</span>
            <span>{word.replace(/[.,!?;:()"]/g, '')}</span>
          </span>
        );
      }
    }
    
    // Return word as-is if no emoji match
    return <span key={startKey + index}>{word}</span>;
  });
};

export function GlobalRecipeResultModal() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isStandalone, setIsStandalone] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [isPersonalizationExpanded, setIsPersonalizationExpanded] = useState(false);
  const [selectedPortions, setSelectedPortions] = useState(1);
  const [nutritionBarsAnimated, setNutritionBarsAnimated] = useState(false);
  const [imageCheckAttempts, setImageCheckAttempts] = useState(0);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [viewportHeight, setViewportHeight] = useState(window.innerHeight);
  
  // 🍽️ REAL DATA: Daily nutrition tracking and targets
  const [nutritionSummary, setNutritionSummary] = useState<DailyNutritionSummary | null>(null);
  const [nutritionTargets, setNutritionTargets] = useState<any>(null);
  const [nutritionDataLoading, setNutritionDataLoading] = useState(true);
  
  const nutritionSectionRef = useRef<HTMLDivElement>(null);
  const databaseService = new DatabaseService();
  
  const { 
    recipeGeneration, 
    hideRecipeResult,
    user,
    inventory,
    toggleFavoriteRecipe,
    loadRecipes,
    addMeal
  } = useNoshStore();

  // Prevent left edge swipe on modal
  useLeftEdgeSwipeDisable({
    pageId: 'RecipeResultModal',
    enabled: recipeGeneration.showResultModal,
    edgeWidth: 50
  });

  // Detect if this is a photo recipe
  const detectedIngredients = recipeGeneration.generationOptions?.detectedIngredients;
  const isPhotoRecipe = detectedIngredients && detectedIngredients.length > 0;

  // Calculate safe mobile browser height
  const isMobileBrowser = isMobile && !isStandalone;
  const safeViewportHeight = isMobileBrowser ? viewportHeight : window.innerHeight;

  // Helper functions to get rounded nutrition targets for display
  const getRoundedTargets = () => {
    if (!nutritionTargets?.macroTargets) {
      return {
        protein: 120,
        carbs: 20, 
        fat: 60,
        fiber: 25
      };
    }
    
    return {
      protein: Math.round(nutritionTargets.macroTargets.protein?.target || 120),
      carbs: Math.round(nutritionTargets.macroTargets.carbs?.target || 20),
      fat: Math.round(nutritionTargets.macroTargets.fat?.target || 60),
      fiber: Math.round(nutritionTargets.macroTargets.fiber?.target || 25)
    };
  };

  // 🍽️ UNIFIED NUTRITION COMPONENT: Create shared nutrition card data
  const getNutritionCardData = () => {
    const roundedTargets = getRoundedTargets();
    
    return [
      {
        id: 'protein',
        name: 'Protein',
        current: formatMacronutrient(recipe.nutrition.protein),
        target: roundedTargets.protein,
        unit: 'g',
        type: 'target',
        color: 'blue',
        bgClass: 'bg-blue-50 dark:bg-blue-900/20',
        borderClass: 'border-blue-200 dark:border-blue-800/30',
        textClass: 'text-blue-600 dark:text-blue-400',
        progressClass: 'bg-gradient-to-r from-blue-500 to-blue-600',
        progressBgClass: isMobile ? 'bg-gray-200 dark:bg-gray-700' : 'bg-blue-100 dark:bg-blue-800/30'
      },
      {
        id: 'carbs',
        name: 'Carbs',
        current: formatMacronutrient(recipe.nutrition.carbs),
        target: roundedTargets.carbs,
        unit: 'g',
        type: 'limit',
        color: 'green',
        bgClass: 'bg-green-50 dark:bg-green-900/20',
        borderClass: 'border-green-200 dark:border-green-800/30',
        textClass: 'text-green-600 dark:text-green-400',
        progressClass: 'bg-gradient-to-r from-green-500 to-green-600',
        progressBgClass: 'bg-gray-200 dark:bg-gray-700'
      },
      {
        id: 'fat',
        name: 'Fat',
        current: formatMacronutrient(recipe.nutrition.fat),
        target: roundedTargets.fat,
        unit: 'g',
        type: 'target',
        color: 'yellow',
        bgClass: 'bg-yellow-50 dark:bg-yellow-900/20',
        borderClass: 'border-yellow-200 dark:border-yellow-800/30',
        textClass: 'text-yellow-600 dark:text-yellow-400',
        progressClass: 'bg-gradient-to-r from-yellow-500 to-yellow-600',
        progressBgClass: 'bg-gray-200 dark:bg-gray-700'
      },
      {
        id: 'fiber',
        name: 'Fiber',
        current: formatMacronutrient(recipe.nutrition.fiber),
        target: roundedTargets.fiber,
        unit: 'g',
        type: 'target',
        color: 'purple',
        bgClass: 'bg-purple-50 dark:bg-purple-900/20',
        borderClass: 'border-purple-200 dark:border-purple-800/30',
        textClass: 'text-purple-600 dark:text-purple-400',
        progressClass: 'bg-gradient-to-r from-purple-500 to-purple-600',
        progressBgClass: isMobile ? 'bg-gray-200 dark:bg-gray-700' : 'bg-purple-100 dark:bg-purple-800/30'
      },
      {
        id: 'sugar',
        name: 'Sugar',
        current: formatMacronutrient(recipe.nutrition.sugar),
        target: 25,
        unit: 'g',
        type: 'limit',
        color: 'gray',
        bgClass: 'bg-gray-50 dark:bg-gray-900/20',
        borderClass: 'border-gray-200 dark:border-gray-800/30',
        textClass: 'text-gray-600 dark:text-gray-400',
        progressClass: 'bg-gradient-to-r from-green-500 to-green-600',
        progressBgClass: 'bg-gray-200 dark:bg-gray-700'
      },
      {
        id: 'sodium',
        name: 'Sodium',
        current: formatMicronutrient(recipe.nutrition.sodium),
        target: 2300,
        unit: 'mg',
        type: 'limit',
        color: 'red',
        bgClass: 'bg-red-50 dark:bg-red-900/20',
        borderClass: 'border-red-200 dark:border-red-800/30',
        textClass: 'text-red-600 dark:text-red-400',
        progressClass: 'bg-gradient-to-r from-green-500 to-green-600',
        progressBgClass: 'bg-gray-200 dark:bg-gray-700'
      }
    ];
  };

  // 🍽️ UNIFIED NUTRITION CARD: Renders individual nutrition cards with consistent logic
  const renderNutritionCard = (card: any, index: number) => {
    const getProgressValue = () => {
      if (card.type === 'range') {
        return Math.min((card.current / 100) * 100, 100);
      }
      return Math.min((card.current / card.target) * 100, 100);
    };

    const getProgressColor = () => {
      if (card.type === 'limit') {
        if (card.current > card.target) return 'bg-gradient-to-r from-red-500 to-red-600';
        if (card.current > card.target * 0.75) return 'bg-gradient-to-r from-amber-500 to-amber-600';
        return card.progressClass;
      }
      if (card.type === 'range') {
        const { min, max } = card.target;
        if (card.current >= min && card.current <= max) return 'bg-gradient-to-r from-green-500 to-green-600';
        return 'bg-gradient-to-r from-amber-500 to-amber-600';
      }
      return card.progressClass;
    };

    const getStatusMessage = () => {
      if (card.type === 'target') {
        if (card.current >= card.target) return '✓ Target met';
        const needed = Math.max(0, card.unit === 'mg' ? formatMicronutrient(card.target - card.current) : formatMacronutrient(card.target - card.current));
        return `Need ${needed}${card.unit} more`;
      }
      if (card.type === 'limit') {
        if (card.current > card.target) return '⚠️ Over limit!';
        if (card.current > card.target * 0.75) return '⚠️ Close to limit';
        return '✓ Under limit';
      }
      if (card.type === 'range') {
        const { min, max } = card.target;
        if (card.current >= min && card.current <= max) return `✓ ${isMobile ? 'In range' : 'In optimal range'}`;
        if (card.current < min) return `${isMobile ? 'Below range' : 'Below optimal range'}`;
        return `${isMobile ? 'Above range' : 'Above optimal range'}`;
      }
      return '';
    };

    const getStatusColor = () => {
      if (card.type === 'target') {
        return card.current >= card.target ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400';
      }
      if (card.type === 'limit') {
        if (card.current > card.target) return 'text-red-600 dark:text-red-400';
        if (card.current > card.target * 0.75) return 'text-amber-600 dark:text-amber-400';
        return 'text-green-600 dark:text-green-400';
      }
      if (card.type === 'range') {
        const { min, max } = card.target;
        if (card.current >= min && card.current <= max) return 'text-green-600 dark:text-green-400';
        return 'text-amber-600 dark:text-amber-400';
      }
      return 'text-gray-600 dark:text-gray-400';
    };

    const getTargetDisplay = () => {
      if (card.type === 'range') {
        const { min, max } = card.target;
        return `/ ${min}-${max}${card.unit} ${isMobile ? '' : 'range'}`;
      }
      const suffix = card.type === 'limit' ? 'limit' : (isMobile ? '' : 'goal');
      return `/ ${card.target}${card.unit} ${suffix}`;
    };

    return (
      <motion.div 
        key={card.id}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 + (index * 0.05) }}
        className={`${card.bgClass} border ${card.borderClass} ${isMobile ? 'p-3' : 'p-4'} rounded-xl ${isMobile ? 'min-h-[120px] flex flex-col justify-between' : ''}`}
      >
        <div className={`flex items-center justify-between ${isMobile ? 'mb-3' : 'mb-2'}`}>
          <span className={`${isMobile ? 'text-sm' : 'text-base'} font-medium text-gray-700 dark:text-gray-300`}>{card.name}</span>
          <div className="text-right">
            <span className={`${isMobile ? 'text-sm' : 'text-base'} font-bold ${card.textClass}`}>{card.current}{card.unit}</span>
            <span className={`${isMobile ? 'text-xs' : 'text-sm'} text-gray-500 dark:text-gray-400 ml-1`}>
              {getTargetDisplay()}
            </span>
          </div>
        </div>
        
        {/* Progress bar */}
        <div className={`w-full ${card.progressBgClass} rounded-full h-2 ${isMobile ? 'mb-3' : 'mb-2'}`}>
          <motion.div 
            className={`h-2 rounded-full ${getProgressColor()}`}
            initial={{ width: 0 }}
            animate={{ width: nutritionBarsAnimated ? `${getProgressValue()}%` : 0 }}
            transition={{ duration: 1.2, delay: 0.1 + (index * 0.1), ease: "easeOut" }}
          />
        </div>
        
        <div className={`flex items-center ${isMobile ? '' : 'text-sm'}`}>
          <span className={`${isMobile ? 'text-xs' : ''} font-medium ${getStatusColor()}`}>
            {getStatusMessage()}
          </span>
        </div>
      </motion.div>
    );
  };

  // 🍽️ REAL DATA: Load daily nutrition tracking and targets
  useEffect(() => {
    const loadNutritionData = async () => {
      if (!recipeGeneration.showResultModal) return;
      
      try {
        setNutritionDataLoading(true);
        
        // Load today's nutrition summary
        const summary = await databaseService.getDailyNutritionSummary();
        setNutritionSummary(summary);
        console.log('📊 [RECIPE MODAL] Loaded nutrition summary:', summary);

        // Calculate nutrition targets if user has body stats
        if (user.age && user.gender && user.height && user.weight && user.activityLevel) {
          const targets = calculateNutritionTargets({
            age: user.age,
            gender: user.gender,
            height: user.height,
            weight: user.weight,
            activityLevel: user.activityLevel,
            goal: user.goal || 'maintain',
            dietaryPreferences: (user.preferences || []) as DietaryPreference[]
          });
          setNutritionTargets(targets);
          console.log('🎯 [RECIPE MODAL] Calculated nutrition targets:', targets);
        } else {
          console.log('⚠️ [RECIPE MODAL] User body stats incomplete, using defaults');
          setNutritionTargets(null);
        }
      } catch (error) {
        console.error('❌ [RECIPE MODAL] Error loading nutrition data:', error);
      } finally {
        setNutritionDataLoading(false);
      }
    };

    loadNutritionData();
  }, [recipeGeneration.showResultModal, user]);

  // Function to scale ingredient amounts based on selected portions
  const scaleIngredientAmount = (amount: string, multiplier: number): string => {
    // Handle fractions and mixed numbers
    const fractionRegex = /(\d+)\/(\d+)/;
    const mixedNumberRegex = /(\d+)\s+(\d+)\/(\d+)/;
    const decimalRegex = /(\d*\.?\d+)/;
    
    // Check for mixed numbers (e.g., "1 1/2 cups")
    const mixedMatch = amount.match(mixedNumberRegex);
    if (mixedMatch) {
      const wholeNumber = parseInt(mixedMatch[1]);
      const numerator = parseInt(mixedMatch[2]);
      const denominator = parseInt(mixedMatch[3]);
      const decimal = wholeNumber + (numerator / denominator);
      const scaled = decimal * multiplier;
      
      // Convert back to mixed number if appropriate
      if (scaled >= 1) {
        const whole = Math.floor(scaled);
        const fraction = scaled - whole;
        if (fraction === 0) {
          return amount.replace(mixedNumberRegex, whole.toString());
        } else if (fraction === 0.5) {
          return amount.replace(mixedNumberRegex, `${whole} 1/2`);
        } else if (fraction === 0.25) {
          return amount.replace(mixedNumberRegex, `${whole} 1/4`);
        } else if (fraction === 0.75) {
          return amount.replace(mixedNumberRegex, `${whole} 3/4`);
        } else {
          return amount.replace(mixedNumberRegex, scaled.toFixed(1));
        }
      } else {
        return amount.replace(mixedNumberRegex, scaled.toFixed(1));
      }
    }
    
    // Check for simple fractions (e.g., "1/2 cup")
    const fractionMatch = amount.match(fractionRegex);
    if (fractionMatch) {
      const numerator = parseInt(fractionMatch[1]);
      const denominator = parseInt(fractionMatch[2]);
      const decimal = numerator / denominator;
      const scaled = decimal * multiplier;
      return amount.replace(fractionRegex, scaled.toFixed(1));
    }
    
    // Check for decimal numbers (e.g., "2.5 lbs", "150g")
    const decimalMatch = amount.match(decimalRegex);
    if (decimalMatch) {
      const number = parseFloat(decimalMatch[1]);
      const scaled = number * multiplier;
      
      // Format nicely - avoid decimals for whole numbers
      const formattedNumber = scaled % 1 === 0 ? scaled.toString() : scaled.toFixed(1);
      return amount.replace(decimalRegex, formattedNumber);
    }
    
    // If no number found, return original amount
    return amount;
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      // Update viewport height for mobile browser compatibility
      setViewportHeight(window.innerHeight);
    };

    // Check if app is running in standalone mode
    const checkStandalone = () => {
      setIsStandalone(isPWAStandalone());
    };

    handleResize();
    checkStandalone();

    window.addEventListener('resize', handleResize);
    // Also listen to orientationchange for mobile browsers
    window.addEventListener('orientationchange', () => {
      setTimeout(handleResize, 100); // Slight delay for orientation change
    });
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  // Add body class for modal backdrop
  useEffect(() => {
    if (recipeGeneration.showResultModal) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }
    
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [recipeGeneration.showResultModal]);

  // Intersection Observer for nutrition bar animations
  useEffect(() => {
    if (!nutritionSectionRef.current || !recipeGeneration.showResultModal) return;
    
    let hasTriggered = false; // Local flag to prevent multiple triggers
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasTriggered) {
            hasTriggered = true;
            // Small delay to ensure smooth animation
            setTimeout(() => {
              setNutritionBarsAnimated(true);
            }, 300);
          }
        });
      },
      {
        threshold: 0.3, // Trigger when 30% of the element is visible
        rootMargin: '0px 0px -50px 0px' // Trigger slightly before fully visible
      }
    );

    observer.observe(nutritionSectionRef.current);

    return () => {
      observer.disconnect();
    };
  }, [recipeGeneration.showResultModal]); // Removed nutritionBarsAnimated and isMobile from dependencies

  // Reset animation state when modal closes
  useEffect(() => {
    if (!recipeGeneration.showResultModal) {
      setNutritionBarsAnimated(false);
    }
  }, [recipeGeneration.showResultModal]);

  // Memory cleanup and garbage collection helper
  useEffect(() => {
    if (recipeGeneration.showResultModal) {
      // Clear any potential memory leaks on modal open
      if (typeof window !== 'undefined' && (window as any).gc) {
        setTimeout(() => {
          try {
            (window as any).gc();
            console.log('🧹 [Global Recipe Result Modal] Manual garbage collection triggered');
          } catch (e) {
            // Ignore GC errors
          }
        }, 1000);
      }
    }
    
    return () => {
      // Clean up on unmount
      setNutritionBarsAnimated(false);
      setIsPersonalizationExpanded(false);
      setSelectedPortions(1);
      setIsSaved(false);
    };
  }, [recipeGeneration.showResultModal]);

  // Reset image check attempts when a new recipe is generated
  useEffect(() => {
    if (recipeGeneration.showResultModal && recipeGeneration.generatedRecipe?.id) {
      setImageCheckAttempts(0);
      console.log('🔄 [MODAL] New recipe detected, resetting image check attempts');
    }
  }, [recipeGeneration.generatedRecipe?.id]);

  // Fetch complete recipe data from database for fresh generations
  useEffect(() => {
    const fetchCompleteRecipeData = async () => {
      const recipe = recipeGeneration.generatedRecipe;
      
      // Only fetch for fresh generations that have an ID but missing dietary_preferences
      if (!recipe?.id || recipe.dietary_preferences || recipe.isSavedRecipe) {
        return;
      }
      
      try {
        console.log('🔄 [MODAL] Fetching complete recipe data from database for fresh generation...');
        
        const { data: completeRecipe, error } = await supabase
          .from('recipes')
          .select('dietary_preferences, user_allergies')
          .eq('id', recipe.id)
          .single();
        
        if (error) {
          console.error('❌ [MODAL] Failed to fetch complete recipe data:', error);
          return;
        }
        
        if (completeRecipe) {
          console.log('✅ [MODAL] Found dietary preferences in database:', completeRecipe.dietary_preferences);
          
          // Update the generated recipe with the complete database data
          const currentState = useNoshStore.getState();
          const updatedGeneratedRecipe = {
            ...currentState.recipeGeneration.generatedRecipe,
            dietary_preferences: completeRecipe.dietary_preferences,
            user_allergies: completeRecipe.user_allergies
          };
          
          useNoshStore.setState(state => ({
            recipeGeneration: {
              ...state.recipeGeneration,
              generatedRecipe: updatedGeneratedRecipe
            }
          }));
          
          console.log('🔄 [MODAL] Updated fresh generation with database dietary preferences');
        }
      } catch (error) {
        console.error('❌ [MODAL] Error fetching complete recipe data:', error);
      }
    };
    
    if (recipeGeneration.showResultModal && recipeGeneration.generatedRecipe?.id) {
      fetchCompleteRecipeData();
    }
  }, [recipeGeneration.generatedRecipe?.id, recipeGeneration.showResultModal]);

  // Auto-refresh image when it becomes available
  useEffect(() => {
    const recipe = recipeGeneration.generatedRecipe;
    if (!recipe?.id || !recipeGeneration.showResultModal) return;
    
    const imageUrl = recipe.imageUrl || recipe.image;
    const hasImage = imageUrl && imageUrl !== '/img/small 1.png' && imageUrl !== '';
    
    // Enhanced polling strategy: 15 attempts over 45 seconds, then extended polling
    const maxInitialAttempts = 15; // Increased from 10
    const maxExtendedAttempts = 30; // Additional attempts with longer intervals
    
    if (!hasImage && imageCheckAttempts < maxExtendedAttempts) {
      const pollForImage = async () => {
        try {
          const isExtendedPolling = imageCheckAttempts >= maxInitialAttempts;
          const attemptDisplay = isExtendedPolling 
            ? `${imageCheckAttempts + 1}/${maxExtendedAttempts} (extended)`
            : `${imageCheckAttempts + 1}/${maxInitialAttempts}`;
            
          console.log(`🔄 [MODAL] Checking for recipe image (attempt ${attemptDisplay})...`);
          
          const { data: updatedRecipe, error } = await supabase
            .from('recipes')
            .select('image_url')
            .eq('id', recipe.id)
            .single();
          
          if (error) {
            console.error('❌ [MODAL] Failed to check for image:', error);
            setImageCheckAttempts(prev => prev + 1);
            return;
          }
          
          if (updatedRecipe?.image_url) {
            console.log('✅ [MODAL] Recipe image found! Updating modal and recipe cards...');
            
            // Update the recipe in the store with the new image
            const currentState = useNoshStore.getState();
            const updatedGeneratedRecipe = {
              ...currentState.recipeGeneration.generatedRecipe,
              imageUrl: updatedRecipe.image_url
            };
            
            // CRITICAL FIX: Also update the recipe in the recipes array so recipe cards show the image
            const updatedRecipes = currentState.recipes.map(r => 
              r.id === recipe.id 
                ? { ...r, imageUrl: updatedRecipe.image_url }
                : r
            );
            
            useNoshStore.setState(state => ({
              recipes: updatedRecipes, // Update recipe cards
              recipeGeneration: {
                ...state.recipeGeneration,
                generatedRecipe: updatedGeneratedRecipe // Update modal
              }
            }));
            
            console.log('🔄 [MODAL] Updated both modal and recipe cards with image URL');
            
            // Reset attempts counter for future recipes
            setImageCheckAttempts(0);
          } else {
            // Increment attempt counter and continue polling
            setImageCheckAttempts(prev => prev + 1);
            
            // Log status at key milestones
            if (imageCheckAttempts + 1 === maxInitialAttempts) {
              console.log('⏰ [MODAL] Initial polling complete, switching to extended polling...');
            } else if (imageCheckAttempts + 1 === maxExtendedAttempts) {
              console.log('⚠️ [MODAL] Image generation taking longer than expected, stopping polling');
            }
          }
        } catch (error) {
          console.error('❌ [MODAL] Error polling for image:', error);
          setImageCheckAttempts(prev => prev + 1);
        }
      };
      
      // Dynamic polling intervals:
      // First 15 attempts: every 3 seconds (45 seconds total)
      // Extended attempts: every 6 seconds (additional 90 seconds)
      const pollInterval = imageCheckAttempts < maxInitialAttempts ? 3000 : 6000;
      const timeoutId = setTimeout(pollForImage, pollInterval);
      return () => clearTimeout(timeoutId);
    } else if (imageCheckAttempts >= maxExtendedAttempts && !hasImage) {
      // Final fallback: show helpful message in console
      console.log('🎨 [MODAL] Image generation completed in background. Recipe saved without image.');
    }
  }, [imageCheckAttempts, recipeGeneration.showResultModal, recipeGeneration.generatedRecipe?.id]);

  if (!recipeGeneration.showResultModal) {
    return null;
  }

  // Use recipe data from generation or saved recipe
  const recipe = recipeGeneration.generatedRecipe || mockRecipe;
  const isSavedRecipe = recipe?.isSavedRecipe || false;
  const generationSource = recipeGeneration.generationOptions?.source || 'generated';

  // 🍽️ REAL DATA: Calculate calorie progress using real daily nutrition tracking
  const currentIntake = nutritionSummary?.totalCalories || 0; // Real current intake from daily tracking
  const dailyGoal = nutritionTargets?.dailyCalories || user.dailyCalorieTarget || 2200; // Real calorie target
  const newTotal = currentIntake + recipe.calories;
  const progressPercentage = Math.min((newTotal / dailyGoal) * 100, 100);

  // Historical dietary preferences from recipe generation context
  const getHistoricalDietPreferences = () => {
    // Check if this is a photo recipe - photo recipes should NOT use stored dietary preferences
    const detectedIngredients = recipeGeneration.generationOptions?.detectedIngredients;
    const isPhotoRecipe = detectedIngredients && detectedIngredients.length > 0;
    

    
    // For photo recipes: Skip stored dietary preferences (user photographed what they want to eat)
    if (!isPhotoRecipe) {
      // PRIORITY 1: Use stored dietary_preferences from database (most reliable historical data for regular recipes)
      if (recipe.dietary_preferences && Array.isArray(recipe.dietary_preferences) && recipe.dietary_preferences.length > 0) {
        return recipe.dietary_preferences;
      }
    }
    
    // PRIORITY 2: Extract historical dietary preferences from recipe tags (fallback for older recipes)
    const recipeTags = recipe.tags || [];
    const historicalPrefs: string[] = [];
    
    // Check for dietary tags in recipe database
    const isKeto = recipeTags.some((tag: string) => tag.toLowerCase().includes('keto'));
    const isLowCarb = recipeTags.some((tag: string) => tag.toLowerCase().includes('low-carb') || tag.toLowerCase().includes('low carb'));
    const isHighProtein = recipeTags.some((tag: string) => tag.toLowerCase().includes('high protein') || tag.toLowerCase().includes('protein'));
    const isVegetarian = recipeTags.some((tag: string) => tag.toLowerCase().includes('vegetarian'));
    const isVegan = recipeTags.some((tag: string) => tag.toLowerCase().includes('vegan'));
    const isGlutenFree = recipeTags.some((tag: string) => tag.toLowerCase().includes('gluten-free'));
    const isBalanced = recipeTags.some((tag: string) => tag.toLowerCase().includes('balanced'));
    
    // Build preferences from historical tags
    if (isKeto) historicalPrefs.push('keto');
    if (isLowCarb) historicalPrefs.push('low-carb');
    if (isHighProtein) historicalPrefs.push('high-protein');
    if (isVegetarian) historicalPrefs.push('vegetarian');
    if (isVegan) historicalPrefs.push('vegan');
    if (isGlutenFree) historicalPrefs.push('gluten-free');
    if (isBalanced) historicalPrefs.push('balanced');
    
    if (historicalPrefs.length > 0) {
      return historicalPrefs;
    }
    
    // PRIORITY 3: Fallback to generation options if available
    if (recipeGeneration.generationOptions) {
      const genOptions = recipeGeneration.generationOptions;
      if (genOptions.dietary && genOptions.dietary !== 'none') {
        return [genOptions.dietary];
      }
    }
    
    // PRIORITY 4: Final fallback to current preferences only if no historical data found
    const prefs = user?.preferences || [];
    return Array.isArray(prefs) ? prefs : [];
  };

  // Format historical dietary preferences for display
  const getUserDietPreferences = () => {
    const dietaryPrefs = getHistoricalDietPreferences();
    if (dietaryPrefs.length === 0) return 'your preferences';
    if (dietaryPrefs.length === 1) return dietaryPrefs[0];
    if (dietaryPrefs.length === 2) return `${dietaryPrefs[0]} and ${dietaryPrefs[1]}`;
    return `${dietaryPrefs.slice(0, -1).join(', ')}, and ${dietaryPrefs[dietaryPrefs.length - 1]}`;
  };

  const getInventoryMatches = () => {
    if (!recipe.ingredients || !inventory) return { count: 0, total: 0, expiring: [] };
    
    // Count recipe ingredients that have inventory matches (not inventory items that match recipes)
    const matchedRecipeIngredients = recipe.ingredients.filter((recipeIng: any) => {
      // Safety check for saved recipes
      if (!recipeIng || typeof recipeIng.name !== 'string') {
        console.warn('Invalid recipe ingredient:', recipeIng);
        return false;
      }
      
      const recipeIngName = recipeIng.name.toLowerCase();
      return inventory.some(item => 
        recipeIngName.includes(item.name.toLowerCase()) || 
        item.name.toLowerCase().includes(recipeIngName)
      );
    });
    
    // Find inventory items that are actually used and check for expiring ones
    const matchedInventoryItems = inventory.filter(item => 
      recipe.ingredients.some((recipeIng: any) => {
        // Safety check for saved recipes
        if (!recipeIng || typeof recipeIng.name !== 'string') {
          return false;
        }
        
        const recipeIngName = recipeIng.name.toLowerCase();
        return recipeIngName.includes(item.name.toLowerCase()) || 
               item.name.toLowerCase().includes(recipeIngName);
      })
    );
    
    // Check for expiring items (within 2 days)
    const expiringItems = matchedInventoryItems.filter(item => {
      if (!item.expiryDate) return false;
      const expiryDate = new Date(item.expiryDate);
      const today = new Date();
      const diffTime = expiryDate.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays <= 2 && diffDays >= 0;
    });

    return {
      count: matchedRecipeIngredients.length, // Count recipe ingredients that have matches
      total: recipe.ingredients.length,
      expiring: expiringItems
    };
  };

  const getProteinBoost = () => {
    const proteinContent = recipe.nutrition?.protein || 0;
    const roundedTargets = getRoundedTargets();
    const dailyProteinTarget = roundedTargets.protein; // Use rounded protein target
    const percentage = Math.round((proteinContent / dailyProteinTarget) * 100);
    
    // Find protein-rich ingredients
    const proteinIngredients = recipe.ingredients.filter((ing: any) => {
      // Safety check for saved recipes
      if (!ing || typeof ing.name !== 'string') {
        return false;
      }
      
      return ['protein', 'meat', 'fish', 'dairy', 'legumes', 'nuts'].some(category => 
        ing.category === category || 
        ['chicken', 'beef', 'fish', 'cheese', 'beans', 'tofu', 'eggs', 'nuts'].some((protein: string) => 
          ing.name.toLowerCase().includes(protein)
        )
      );
    });

    return {
      amount: proteinContent,
      percentage,
      ingredients: proteinIngredients.slice(0, 2) // Top 2 protein sources
    };
  };

  const getCalorieTargetMatch = () => {
    const targetCalories = dailyGoal / 3; // Rough estimate for one meal
    const difference = Math.abs(recipe.calories - targetCalories);
    const isWithinRange = difference <= 100; // Within 100 calories is good
    
    return {
      isWithinRange,
      target: Math.round(targetCalories),
      actual: recipe.calories,
      difference: Math.round(difference)
    };
  };

  // Get historical allergies that were accommodated during recipe generation
  const getHistoricalAllergies = () => {
    // PRIORITY 1: Use stored user_allergies from database (most reliable historical data)
    if (recipe.user_allergies && Array.isArray(recipe.user_allergies) && recipe.user_allergies.length > 0) {
      return recipe.user_allergies;
    }
    
    // PRIORITY 2: Extract allergen-safe tags to determine what was accommodated (fallback for older recipes)
    const recipeTags = recipe.tags || [];
    const historicalAllergies: string[] = [];
    
    recipeTags.forEach((tag: string) => {
      const tagLower = tag.toLowerCase();
      if (tagLower.includes('dairy-free') || tagLower.includes('milk-free')) {
        historicalAllergies.push('dairy');
      }
      if (tagLower.includes('gluten-free')) {
        historicalAllergies.push('gluten');
      }
      if (tagLower.includes('nut-free')) {
        historicalAllergies.push('nuts');
      }
      if (tagLower.includes('soy-free')) {
        historicalAllergies.push('soy');
      }
      if (tagLower.includes('egg-free')) {
        historicalAllergies.push('eggs');
      }
      if (tagLower.includes('peanut-free')) {
        historicalAllergies.push('peanuts');
      }
      if (tagLower.includes('shellfish-free')) {
        historicalAllergies.push('shellfish');
      }
      if (tagLower.includes('fish-free')) {
        historicalAllergies.push('fish');
      }
      // Check for custom allergy names in tags
      const userAllergies = user?.allergies || [];
      userAllergies.forEach((allergy: string) => {
        if (tagLower.includes(allergy.toLowerCase()) && !historicalAllergies.includes(allergy.toLowerCase())) {
          historicalAllergies.push(allergy.toLowerCase());
        }
      });
    });
    
    if (historicalAllergies.length > 0) {
      return historicalAllergies;
    }
    
    // PRIORITY 3: Fallback to generation options if available
    if (recipeGeneration.generationOptions) {
      // If we have generation options, use current allergies (recent generation)
      return user?.allergies || [];
    }
    
    return [];
  };

  // Enhanced personalization data with historical context
  const getAllergyAdaptations = () => {
    const currentAllergies = user?.allergies || [];
    const historicalAllergies = getHistoricalAllergies();
    
    // Use historical allergies for the main accommodation display
    const allergies = historicalAllergies.length > 0 ? historicalAllergies : currentAllergies;
    
    if (allergies.length === 0 && currentAllergies.length === 0) {
      return { hasAllergies: false, adaptations: [], hasViolations: false, violations: [], isHistorical: false };
    }
    
    // Comprehensive allergen detection patterns
    const allergenPatterns: Record<string, { keywords: string[], substitutes: string[] }> = {
      'milk': {
        keywords: ['milk', 'dairy', 'cheese', 'butter', 'cream', 'lactose', 'casein', 'whey', 'yogurt'],
        substitutes: ['coconut milk', 'almond milk', 'oat milk', 'soy milk', 'nutritional yeast']
      },
      'dairy': {
        keywords: ['milk', 'dairy', 'cheese', 'butter', 'cream', 'lactose', 'casein', 'whey', 'yogurt'],
        substitutes: ['coconut milk', 'almond milk', 'oat milk', 'nutritional yeast']
      },
      'gluten': {
        keywords: ['wheat', 'flour', 'bread', 'pasta', 'gluten', 'barley', 'rye'],
        substitutes: ['rice flour', 'almond flour', 'gluten-free pasta', 'quinoa']
      },
      'nuts': {
        keywords: ['almond', 'walnut', 'cashew', 'pecan', 'hazelnut', 'peanut', 'pistachio'],
        substitutes: ['seeds', 'coconut', 'roasted chickpeas']
      },
      'soy': {
        keywords: ['soy', 'tofu', 'tempeh', 'miso', 'soybean', 'edamame'],
        substitutes: ['coconut aminos', 'salt', 'garlic powder']
      },
      'eggs': {
        keywords: ['egg', 'mayonnaise', 'meringue'],
        substitutes: ['applesauce', 'flax eggs', 'aquafaba']
      },
      'peanuts': {
        keywords: ['peanut', 'groundnut'],
        substitutes: ['sunflower seeds', 'pumpkin seeds', 'coconut']
      }
    };
    
    // Check for allergen violations in recipe ingredients
    const violations: Array<{ allergen: string, ingredient: string, reason: string }> = [];
    const safeSubstitutions: Array<{ allergen: string, substitutes: any[], avoided: boolean }> = [];
    
    allergies.forEach((allergy: string) => {
      const pattern = allergenPatterns[allergy.toLowerCase()];
      if (!pattern) return;
      
      // Check each recipe ingredient for allergen violations
      recipe.ingredients.forEach((ing: any) => {
        // Safety check for saved recipes
        if (!ing || typeof ing.name !== 'string') {
          console.warn('Invalid recipe ingredient for allergy check:', ing);
          return;
        }
        
        const ingredientName = ing.name.toLowerCase();
        const hasAllergen = pattern.keywords.some(keyword => 
          ingredientName.includes(keyword.toLowerCase())
        );
        
        if (hasAllergen) {
          // Critical safety violation detected
          violations.push({
            allergen: allergy,
            ingredient: ing.name,
            reason: `Contains ${allergy} - unsafe for your allergy`
          });
        }
      });
      
      // Look for safe substitutes that were used
      const usedSubstitutes = recipe.ingredients.filter((ing: any) => {
        // Safety check for saved recipes
        if (!ing || typeof ing.name !== 'string') {
          return false;
        }
        
        return pattern.substitutes.some((sub: string) => 
          ing.name.toLowerCase().includes(sub.toLowerCase()) && 
          !pattern.keywords.some(keyword => ing.name.toLowerCase().includes(keyword.toLowerCase()))
        );
      });
      
      if (usedSubstitutes.length > 0) {
        safeSubstitutions.push({
          allergen: allergy,
          substitutes: usedSubstitutes,
          avoided: true
        });
      }
    });
    
    return {
      hasAllergies: allergies.length > 0,
      allergies: allergies,
      adaptations: safeSubstitutions,
      hasViolations: violations.length > 0,
      violations: violations,
      isHistorical: historicalAllergies.length > 0,
      currentAllergies: currentAllergies,
      historicalAllergies: historicalAllergies
    };
  };

  const getBodyStatsOptimization = () => {
    const height = user?.height || 170; // cm
    const weight = user?.weight || 70; // kg
    const age = user?.age || 30;
    const gender = user?.gender || 'other';
    const activityLevel = user?.activityLevel || 'moderate';
    
    // Calculate BMR using Mifflin-St Jeor Equation
    let bmr = 0;
    if (gender === 'male') {
      bmr = 88.362 + (13.397 * weight) + (4.799 * height) - (5.677 * age);
    } else if (gender === 'female') {
      bmr = 447.593 + (9.247 * weight) + (3.098 * height) - (4.330 * age);
    } else {
      bmr = ((88.362 + (13.397 * weight) + (4.799 * height) - (5.677 * age)) + 
             (447.593 + (9.247 * weight) + (3.098 * height) - (4.330 * age))) / 2;
    }
    
    const activityMultipliers = {
      'sedentary': 1.2,
      'light': 1.375,
      'moderate': 1.55,
      'active': 1.725,
      'very_active': 1.9
    };
    
    const tdee = bmr * (activityMultipliers[activityLevel] || 1.55);
    const mealCalories = Math.round(tdee / 3); // Rough meal target
    
    return {
      bmr: Math.round(bmr),
      tdee: Math.round(tdee),
      mealTarget: mealCalories,
      isOptimized: Math.abs(recipe.calories - mealCalories) <= 150,
      heightWeight: `${height}cm, ${weight}kg`,
      hasBodyStats: height && weight && age
    };
  };

  // Get ingredient location from inventory or fallback for common items
  const getIngredientLocation = (ingredientName: string): { location: string; emoji: string; bgColor: string; textColor: string } => {
    // Check if this is a photo recipe using store data
    const detectedIngredients = recipeGeneration.generationOptions?.detectedIngredients;
    const isPhotoRecipe = detectedIngredients && detectedIngredients.length > 0;
    
    // For photo recipes, all ingredients should show "Photo detected"
    if (isPhotoRecipe) {
      return { 
        location: 'Detected by Photo', 
        emoji: '', 
        bgColor: 'bg-transparent', 
        textColor: 'text-gray-400 dark:text-gray-500' 
      };
    }
    
    if (!inventory) {
      return { location: 'Common Items', emoji: '🏪', bgColor: 'bg-gray-100 dark:bg-gray-700', textColor: 'text-gray-600 dark:text-gray-300' };
    }

    // Find matching inventory item
    const matchedItem = inventory.find(item => {
      const itemName = item.name.toLowerCase();
      const recipeIngName = ingredientName.toLowerCase();
      return itemName.includes(recipeIngName) || recipeIngName.includes(itemName);
    });

    if (matchedItem) {
      // Use exact location from inventory with matching styling from inventory page
      switch (matchedItem.location) {
        case 'fridge':
          return { location: 'Fridge', emoji: '🧊', bgColor: 'bg-blue-50 dark:bg-blue-900/20', textColor: 'text-blue-700 dark:text-blue-300' };
        case 'pantry':
          return { location: 'Pantry', emoji: '🏠', bgColor: 'bg-yellow-50 dark:bg-yellow-900/20', textColor: 'text-yellow-700 dark:text-yellow-300' };
        case 'freezer':
          return { location: 'Freezer', emoji: '❄️', bgColor: 'bg-cyan-50 dark:bg-cyan-900/20', textColor: 'text-cyan-700 dark:text-cyan-300' };
        default:
          return { location: 'Pantry', emoji: '🏠', bgColor: 'bg-yellow-50 dark:bg-yellow-900/20', textColor: 'text-yellow-700 dark:text-yellow-300' };
      }
    }

    // Fallback for common ingredients not in inventory
    const commonIngredients = [
      'salt', 'pepper', 'black pepper', 'white pepper', 'garlic powder', 'onion powder',
      'paprika', 'cumin', 'oregano', 'thyme', 'basil', 'rosemary', 'bay leaves',
      'cinnamon', 'vanilla', 'baking powder', 'baking soda', 'flour', 'sugar',
      'olive oil', 'vegetable oil', 'vinegar', 'lemon juice', 'lime juice'
    ];

    const isCommonIngredient = commonIngredients.some(common => 
      ingredientName.toLowerCase().includes(common)
    );

    if (isCommonIngredient) {
      return { location: 'Common Items', emoji: '🏪', bgColor: 'bg-gray-100 dark:bg-gray-700', textColor: 'text-gray-600 dark:text-gray-300' };
    }

    // Default fallback - suggest shopping
    return { location: 'Shopping List', emoji: '🛒', bgColor: 'bg-orange-100 dark:bg-orange-900/20', textColor: 'text-orange-700 dark:text-orange-300' };
  };

  const getMacroGoalsAlignment = () => {
    const nutrition = recipe.nutrition || {};
    
    // Check if this is a photo recipe (dynamic detection)
    const detectedIngredients = recipeGeneration.generationOptions?.detectedIngredients;
    const isPhotoRecipe = detectedIngredients && detectedIngredients.length > 0;
    
    let dietType: string;
    let targets: any;
    let historicalGoal: string;
    let historicalPreferences: string[];
    
    if (isPhotoRecipe) {
      // For photo recipes: analyze actual recipe content instead of preferences
      const carbs = nutrition.carbs || 0;
      const fat = nutrition.fat || 0;
      const protein = nutrition.protein || 0;
      const totalMacros = carbs + fat + protein;
      
      // Use current user goal for photo recipes since they're analyzed dynamically
      historicalGoal = user?.goal || 'maintain';
      historicalPreferences = getHistoricalDietPreferences();
      
      if (totalMacros > 0) {
        const carbPercentage = (carbs / totalMacros) * 100;
        const fatPercentage = (fat / totalMacros) * 100;
        
        // Dynamic diet type based on actual macro content
        if (carbs <= 20 && fatPercentage >= 60) {
          dietType = 'Keto';
          targets = {
            protein: { min: 20, max: 30, unit: 'g' },
            carbs: { min: 5, max: 20, unit: 'g' },
            fat: { min: 60, max: 80, unit: 'g' }
          };
        } else if (carbs <= 30 && carbPercentage <= 25) {
          dietType = 'Low-Carb';
          targets = {
            protein: { min: 25, max: 35, unit: 'g' },
            carbs: { min: 15, max: 30, unit: 'g' },
            fat: { min: 35, max: 45, unit: 'g' }
          };
        } else if (protein >= 25 && (protein / totalMacros) * 100 >= 30) {
          dietType = 'High-Protein';
          targets = {
            protein: { min: 25, max: 40, unit: 'g' },
            carbs: { min: 20, max: 40, unit: 'g' },
            fat: { min: 20, max: 35, unit: 'g' }
          };
        } else {
          dietType = 'Balanced';
          targets = {
            protein: { min: 20, max: 35, unit: 'g' },
            carbs: { min: 45, max: 65, unit: 'g' },
            fat: { min: 20, max: 35, unit: 'g' }
          };
        }
      } else {
        // Fallback if no nutrition data
        dietType = 'Photo-Based';
        targets = {
          protein: { min: 15, max: 40, unit: 'g' },
          carbs: { min: 10, max: 70, unit: 'g' },
          fat: { min: 10, max: 50, unit: 'g' }
        };
      }
    } else {
      // For regular recipes: use HISTORICAL dietary preferences from database
      historicalPreferences = getHistoricalDietPreferences();
      
      // Use current user goal (goal isn't stored with recipe historically)
      historicalGoal = user?.goal || 'maintain';
      
      // Detect diet type from stored dietary preferences (use exact match for primary diet)
      const primaryDiet = historicalPreferences.length > 0 ? historicalPreferences[0].toLowerCase() : 'balanced';
      
      // Set default targets
      targets = {
        protein: { min: 20, max: 35, unit: 'g' },
        carbs: { min: 45, max: 65, unit: 'g' },
        fat: { min: 20, max: 35, unit: 'g' }
      };
      
      // Map stored dietary preferences to diet types and targets
      if (primaryDiet.includes('keto')) {
        dietType = 'Keto';
        targets = {
          protein: { min: 20, max: 30, unit: 'g' },
          carbs: { min: 5, max: 20, unit: 'g' },
          fat: { min: 60, max: 80, unit: 'g' }
        };
      } else if (primaryDiet.includes('low-carb') || primaryDiet.includes('low carb')) {
        dietType = 'Low-Carb';
        targets = {
          protein: { min: 25, max: 35, unit: 'g' },
          carbs: { min: 15, max: 30, unit: 'g' },
          fat: { min: 35, max: 45, unit: 'g' }
        };
      } else if (primaryDiet.includes('protein')) {
        dietType = 'High-Protein';
        targets = {
          protein: { min: 25, max: 40, unit: 'g' },
          carbs: { min: 20, max: 40, unit: 'g' },
          fat: { min: 20, max: 35, unit: 'g' }
        };
      } else if (primaryDiet.includes('paleo')) {
        dietType = 'Paleo';
        targets = {
          protein: { min: 25, max: 35, unit: 'g' },
          carbs: { min: 30, max: 40, unit: 'g' },
          fat: { min: 35, max: 45, unit: 'g' }
        };
      } else if (primaryDiet.includes('carnivore')) {
        dietType = 'Carnivore';
        targets = {
          protein: { min: 30, max: 40, unit: 'g' },
          carbs: { min: 0, max: 5, unit: 'g' },
          fat: { min: 60, max: 70, unit: 'g' }
        };
      } else if (primaryDiet.includes('vegan')) {
        dietType = 'Vegan';
        targets = {
          protein: { min: 22, max: 30, unit: 'g' },
          carbs: { min: 50, max: 60, unit: 'g' },
          fat: { min: 20, max: 25, unit: 'g' }
        };
      } else if (primaryDiet.includes('vegetarian')) {
        dietType = 'Vegetarian';
        targets = {
          protein: { min: 18, max: 25, unit: 'g' },
          carbs: { min: 50, max: 60, unit: 'g' },
          fat: { min: 25, max: 30, unit: 'g' }
        };
      } else if (primaryDiet.includes('mediterranean')) {
        dietType = 'Mediterranean';
        targets = {
          protein: { min: 18, max: 25, unit: 'g' },
          carbs: { min: 40, max: 50, unit: 'g' },
          fat: { min: 35, max: 40, unit: 'g' }
        };
      } else {
        dietType = 'Balanced';
      }
    }
    
    const alignment = {
      protein: nutrition.protein >= targets.protein.min && nutrition.protein <= targets.protein.max,
      carbs: nutrition.carbs >= targets.carbs.min && nutrition.carbs <= targets.carbs.max,
      fat: nutrition.fat >= targets.fat.min && nutrition.fat <= targets.fat.max
    };
    
    return {
      targets,
      alignment,
      dietType,
      goalOptimized: historicalGoal === 'lose' ? 'weight loss' : historicalGoal === 'gain' ? 'muscle gain' : 'maintenance',
      isPhotoRecipe,
      historicalPreferences
    };
  };

  const inventoryData = getInventoryMatches();
  const proteinData = getProteinBoost();
  const calorieData = getCalorieTargetMatch();
  const allergyData = getAllergyAdaptations();
  const bodyStatsData = getBodyStatsOptimization();
  const macroData = getMacroGoalsAlignment();

  const handleClose = () => {
    console.log('🔍 [Global Recipe Result Modal] Closing modal');
    hideRecipeResult();
  };

  const handleSaveRecipe = async () => {
    console.log('💾 Saving recipe to favorites');
    console.log('🔍 Recipe object:', recipe);
    console.log('🔍 Recipe ID:', recipe?.id);
    console.log('🔍 Recipe spice_level:', recipe?.spiceLevel);
    console.log('🔍 Recipe servings:', recipe?.servings);
    console.log('🔍 Recipe difficulty:', recipe?.difficulty);
    
    if (!recipe?.id) {
      console.error('❌ Cannot save recipe: no recipe ID found');
      console.error('❌ Recipe data:', {
        hasRecipe: !!recipe,
        recipeName: recipe?.name,
        recipeKeys: recipe ? Object.keys(recipe) : []
      });
      return;
    }
    
    try {
      console.log('🔄 Starting recipe save process...');
      
      // First, let's verify the recipe exists in the database
      console.log('🔍 Checking if recipe exists in database...');
      
      // Mark recipe as favorite
      console.log('🔄 Toggling favorite status...');
      await toggleFavoriteRecipe(recipe.id);
      console.log('✅ Favorite toggle completed');
      
      // Update local state to show as saved
      setIsSaved(true);
      console.log('✅ Local state updated');
      
      // Refresh recipes list to show the saved recipe (force refresh to bypass cache)
      console.log('🔄 Refreshing recipes list...');
      console.log('📊 Current recipes count BEFORE refresh:', useNoshStore.getState().recipes.length);
      await loadRecipes(true); // Force refresh to bypass cache
      console.log('📊 Current recipes count AFTER refresh:', useNoshStore.getState().recipes.length);
      console.log('✅ Recipes list refreshed - all pages should now auto-update');
      
      console.log('✅ Recipe saved to favorites successfully');
    } catch (error) {
      console.error('❌ Failed to save recipe to favorites:', error);
      console.error('❌ Error details:', {
        message: (error as Error).message,
        name: (error as Error).name,
        stack: (error as Error).stack
      });
      
      // If it's a 406 error, let's log more details
      if ((error as Error).message && (error as Error).message.includes('406')) {
        console.error('🚨 406 Error Details:', {
          recipeId: recipe?.id,
          spiceLevel: recipe?.spiceLevel,
          difficulty: recipe?.difficulty,
          errorMessage: (error as Error).message
        });
      }
    }
  };

  const handleLogMeal = async () => {
    if (!recipe) {
      console.error('❌ No recipe available to log as meal');
      return;
    }

    try {
      console.log('📝 Logging recipe as meal to tracker...');
      console.log('🔍 Recipe image data:', {
        hasImage: !!recipe.image,
        hasImageUrl: !!recipe.imageUrl,
        imageValue: recipe.image,
        imageUrlValue: recipe.imageUrl,
        imageLength: recipe.image?.length || 0,
        imageUrlLength: recipe.imageUrl?.length || 0
      });
      
      // Always log as 1 serving when logging as a meal
      const servings = 1;
      
      // Create a meal object from recipe data 
      const mealData = {
        mealName: recipe.name || recipe.title,
        imageUrl: (recipe.image && recipe.image.trim() !== '') ? recipe.image : 
                  (recipe.imageUrl && recipe.imageUrl.trim() !== '') ? recipe.imageUrl : 
                  '/food.jpg',
        servingSize: `${servings} serving${servings !== 1 ? 's' : ''}`,
        confidence: 95, // High confidence for manually logged recipes
        calories: formatCalories((recipe.calories || 0) / (recipe.servings || 1) * servings),
        protein: formatMacronutrient((recipe.nutrition?.protein || 0) / (recipe.servings || 1) * servings),
        carbs: formatMacronutrient((recipe.nutrition?.carbs || 0) / (recipe.servings || 1) * servings),
        fat: formatMacronutrient((recipe.nutrition?.fat || 0) / (recipe.servings || 1) * servings),
        fiber: formatMacronutrient((recipe.nutrition?.fiber || 0) / (recipe.servings || 1) * servings),
        sugar: formatMacronutrient((recipe.nutrition?.sugar || 0) / (recipe.servings || 1) * servings),
        sodium: formatMicronutrient((recipe.nutrition?.sodium || 0) / (recipe.servings || 1) * servings),
        ingredients: Array.isArray(recipe.ingredients) 
          ? recipe.ingredients.map((ing: any) => typeof ing === 'string' ? ing : ing.name)
          : [],
        aiSuggestions: [`Cooked from recipe: ${recipe.name || recipe.title}`],
        source: 'recipe' as const // Mark this meal as coming from a recipe
      };

      // Add meal to tracker (this will auto-log to nutrition tracking)
      await addMeal(mealData);
      
      console.log('✅ Recipe successfully logged as meal');
      
      // Close modal after successful logging
      hideRecipeResult();
      
    } catch (error) {
      console.error('❌ Failed to log recipe as meal:', error);
    }
  };

  const handleDeleteRecipe = async () => {
    if (!recipe?.id) {
      console.error('❌ Cannot delete recipe: no recipe ID found');
      return;
    }

    setIsDeleting(true);
    
    try {
      console.log('🗑️ Deleting recipe:', recipe.id);
      
      // Get current user from auth
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      
      if (!currentUser) {
        throw new Error('User not authenticated');
      }

      // Step 1: Get recipe details before deletion to extract image path
      const { data: recipeData, error: fetchError } = await supabase
        .from('recipes')
        .select('image_url')
        .eq('id', recipe.id)
        .eq('user_id', currentUser.id)
        .single();

      if (fetchError) {
        console.warn('⚠️ Could not fetch recipe details for image cleanup:', fetchError);
      }

      // Step 2: Delete associated image from storage if it exists
      if (recipeData?.image_url) {
        try {
          console.log('🖼️ Deleting associated image:', recipeData.image_url);
          
          // Extract the file path from the URL
          // Recipe images are stored in 'recipe-images' bucket with path format: recipe-images/userId/filename
          const url = new URL(recipeData.image_url);
          const pathParts = url.pathname.split('/');
          
          // Find the bucket and path in the URL (format: /storage/v1/object/public/recipe-images/userId/filename)
          const bucketIndex = pathParts.findIndex(part => part === 'recipe-images');
          if (bucketIndex > 0 && bucketIndex < pathParts.length - 1) {
            // Extract the file path after the bucket name
            const filePath = pathParts.slice(bucketIndex + 1).join('/');
            console.log('📁 Extracted file path for deletion:', filePath);
            
            const { error: storageError } = await supabase.storage
              .from('recipe-images')
              .remove([filePath]);
            
            if (storageError) {
              console.warn('⚠️ Failed to delete image from storage:', storageError);
              // Continue with recipe deletion even if image deletion fails
            } else {
              console.log('✅ Successfully deleted image from storage');
            }
          } else {
            console.warn('⚠️ Could not extract file path from image URL:', recipeData.image_url);
          }
        } catch (imageError) {
          console.warn('⚠️ Error during image deletion:', imageError);
          // Continue with recipe deletion even if image deletion fails
        }
      }
      
      // Step 3: Delete the recipe from the database
      // Note: user_recipes table will automatically cascade delete due to foreign key constraints
      const { error } = await supabase
        .from('recipes')
        .delete()
        .eq('id', recipe.id)
        .eq('user_id', currentUser.id);

      if (error) {
        throw error;
      }

      console.log('✅ Recipe and associated data deleted successfully');
      
      // Step 4: Refresh recipes list to remove the deleted recipe
      await loadRecipes(true);
      
      // Step 5: Close the modal
      hideRecipeResult();
      
    } catch (error) {
      console.error('❌ Failed to delete recipe:', error);
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const difficultyColors: Record<string, string> = {
    'Easy': 'text-green-600 bg-green-50',
    'Medium': 'text-yellow-600 bg-yellow-50',
    'Hard': 'text-red-600 bg-red-50'
  };

  const modal = (
    <AnimatePresence>
      {recipeGeneration.showResultModal && (
        <div 
          data-recipe-result-modal
          className={`fixed inset-0 flex items-center justify-center ${
            isMobile && isStandalone ? 'z-[9999]' : 'z-50'
          } ${isMobile ? 'overflow-hidden' : ''}`} 
          style={{ 
            backdropFilter: 'blur(8px)',
            ...(isMobile ? {
              touchAction: 'pan-y',
              overflowX: 'hidden',
              overscrollBehavior: 'none',
              overscrollBehaviorY: 'none',
              height: isMobileBrowser ? `${safeViewportHeight}px` : '100vh',
              maxHeight: isMobileBrowser ? `${safeViewportHeight}px` : '100vh'
            } : {})
          }}>
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/60"
            onClick={handleClose}
          />
          
          {/* Modal Content */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.3, ease: 'linear' }}
            className={`relative ${
              isMobile 
                ? 'w-full mx-0 rounded-none flex flex-col bg-background' 
                : 'w-full max-w-4xl mx-4 max-h-[90vh] bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-gray-200 dark:border-slate-700'
            }`}
            style={isMobile ? {
              touchAction: 'pan-y pinch-zoom',
              overflowX: 'hidden',
              height: isMobileBrowser ? `${safeViewportHeight}px` : '100vh',
              position: 'relative',
              maxHeight: isMobileBrowser ? `${safeViewportHeight}px` : '100vh'
            } : undefined}

          >
            {/* Header */}
            <div className={`sticky top-0 ${
              isMobile 
                ? 'bg-background px-4 py-4 border-b border-gray-200 dark:border-slate-700 z-50' 
                : 'bg-white dark:bg-slate-800 p-6 border-b border-gray-200 dark:border-slate-700 rounded-t-2xl'
            }`}
            style={isMobile ? {
              touchAction: 'none',
              overscrollBehavior: 'none'
            } : undefined}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-red-500 rounded-xl flex items-center justify-center">
                    <ChefHat size={20} className="text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h2 className="text-xl font-heading font-semibold text-foreground">
                        Recipe Generated!
                      </h2>
                      {/* Delete Button - Positioned to the right of the text */}
                      {isSavedRecipe && (
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => setShowDeleteConfirm(true)}
                          className="rounded-full text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 ml-4"
                        >
                          <Trash2 size={20} />
                        </Button>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">AI-crafted for your preferences</p>
                  </div>
                </div>
                <Button 
                  variant="ghost" 
                  size="icon"
                  onClick={handleClose}
                  className="rounded-full"
                >
                  <X size={20} />
                </Button>
              </div>
            </div>

            {/* Content */}
            <div className={`${
              isMobile 
                ? 'flex-1 overflow-y-auto bg-background' 
                : 'overflow-y-auto max-h-[70vh]'
            }`}
            style={isMobile ? {
              touchAction: 'pan-y',
              overflowX: 'hidden',
              overscrollBehavior: 'contain',
              WebkitOverflowScrolling: 'touch'
            } : undefined}
            >
              
              {/* Recipe Header */}
              <div className="p-6 pb-4">
                <div className="flex flex-col lg:flex-row gap-6">
                  {/* Recipe Image */}
                  <div className="lg:w-80 flex-shrink-0">
                    <div className="aspect-square w-full max-w-80 mx-auto lg:mx-0 rounded-2xl overflow-hidden">
                      {/* Recipe Image with Smart Loading State */}
                      {(() => {
                        const imageUrl = recipe.imageUrl || recipe.image;
                        const hasImage = imageUrl && imageUrl !== '/img/small 1.png' && imageUrl !== '';
                        
                        if (hasImage) {
                          // Show actual recipe image
                          return (
                                        <CachedImage 
              src={getOptimizedImageUrl(imageUrl, 'recipe-modal-large', { 
                fallbackUrl: '/img/small 1.png',
                quality: 90 
              })}
              alt={recipe.name}
              className="w-full h-full object-cover"
              loading="lazy"
            />
                          );
                        } else {
                          // Show beautiful placeholder while image is being generated
                          return (
                            <div className="w-full h-full bg-gray-50 dark:bg-gray-800 flex flex-col items-center justify-center p-6 text-center">
                              {/* Robot Animation */}
                              <motion.div
                                initial={{ scale: 0.8, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ duration: 0.5 }}
                                className="mb-4"
                              >
                                <img 
                                  src="/robo_run.webp" 
                                  alt="Chef robot preparing dish" 
                                  className="w-24 h-24 object-contain"
                                />
                              </motion.div>
                              
                              {/* Chef Message */}
                              <motion.div
                                initial={{ y: 10, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                transition={{ delay: 0.3, duration: 0.5 }}
                                className="space-y-2"
                              >
                                <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                                  Our Chef is making his way to bring the dish to you
                                </h3>
                              </motion.div>
                            </div>
                          );
                        }
                      })()}
                    </div>
                  </div>

                  {/* Recipe Info */}
                  <div className="flex-1 space-y-4">
                    <div>
                      <h1 className="text-2xl lg:text-3xl font-heading font-bold text-foreground mb-2">
                        {recipe.name}
                      </h1>
                      <p className="text-muted-foreground leading-relaxed">
                        {recipe.description || `A delicious ${recipe.name?.toLowerCase()} recipe with ${recipe.ingredients?.length || 0} ingredients.`}
                      </p>
                    </div>

                    {/* Enhanced Stats & Tags Row */}
                    <div className="space-y-3">
                      {/* All Tags in One Row */}
                      <div className="flex flex-wrap gap-2">
                        {/* Calories */}
                        <div className="flex items-center bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300 rounded-md px-2 py-1">
                          <span className="font-medium text-xs">{formatCalories(recipe.calories)} cal</span>
                        </div>
                        
                        {/* Cook Time */}
                        <div className="flex items-center bg-sky-50 dark:bg-sky-900/20 text-sky-700 dark:text-sky-300 rounded-md px-2 py-1">
                          <span className="font-medium text-xs">{recipe.cookTime} min</span>
                        </div>
                        
                        {/* Difficulty */}
                        {(() => {
                          const options = recipeGeneration.generationOptions;
                          let displayDifficulty = recipe.difficulty;
                          let difficultyColor = difficultyColors[recipe.difficulty] || 'text-gray-600 bg-gray-50';
                          
                          // Map AI response to proper difficulty terms
                          if (options?.difficulty) {
                            if (options.difficulty === 'easy') {
                              displayDifficulty = 'Easy';
                              difficultyColor = 'text-green-600 bg-green-50 dark:bg-green-900/20';
                            } else if (options.difficulty === 'medium') {
                              displayDifficulty = 'Medium';
                              difficultyColor = 'text-yellow-600 bg-yellow-50 dark:bg-yellow-900/20';
                            } else if (options.difficulty === 'hard') {
                              displayDifficulty = 'Expert';
                              difficultyColor = 'text-red-600 bg-red-50 dark:bg-red-900/20';
                            }
                          }
                          
                          return (
                            <div className={`flex items-center rounded-md px-2 py-1 ${difficultyColor}`}>
                              <span className="font-medium text-xs">{displayDifficulty}</span>
                            </div>
                          );
                        })()}

                        {/* Saved Recipe Indicator */}
                        {isSavedRecipe && (
                          <div className="flex items-center bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded-md px-2 py-1 text-xs font-medium">
                            <span>📖 Saved Recipe</span>
                          </div>
                        )}

                        {/* Unified Tag System - Shows database tags with generation options as fallback */}
                        {(() => {
                          // Collect all tags from both sources
                          const databaseTags = recipe.tags && Array.isArray(recipe.tags) ? recipe.tags : [];
                          const generationOptions = recipeGeneration.generationOptions;
                          
                          // Build generation option tags as fallback
                          const generationTags = [];
                          if (generationOptions && !isSavedRecipe) {
                            if (generationOptions.mealType && generationOptions.mealType !== 'any') {
                              generationTags.push(generationOptions.mealType);
                            }
                            if (generationOptions.cuisineStyle && generationOptions.cuisineStyle !== 'any') {
                              generationTags.push(generationOptions.cuisineStyle);
                            }
                            if (generationOptions.spiceLevel) {
                              generationTags.push(`${generationOptions.spiceLevel} spice`);
                            }
                            // Only add source tags for non-photo recipes
                            if (generationOptions.source && generationOptions.source !== 'photo') {
                              generationTags.push(generationOptions.source === 'inventory' ? 'Inventory only' : 'Mixed ingredients');
                            }
                            if (generationOptions.additionalProtein) generationTags.push('Extra protein');
                            if (generationOptions.additionalVegetables) generationTags.push('Extra vegetables');
                            if (generationOptions.healthyFocus) generationTags.push('Healthy focus');
                            if (generationOptions.budgetFriendly) generationTags.push('Budget friendly');
                            if (generationOptions.prioritizeExpiring) generationTags.push('Expiring first');
                          }
                          
                          // Combine and deduplicate tags with smart semantic deduplication
                          const allTags: string[] = [...databaseTags, ...generationTags];
                          const uniqueTags: string[] = deduplicateSmartTags(allTags);
                          
                          // Filter to show only important tags
                          const filteredTags = uniqueTags.filter((tag: string) => {
                            const tagLower = tag.toLowerCase();
                            
                            // Skip basic tags that are already shown elsewhere
                            const skipTags = ['easy', 'medium', 'hard', 'saved recipe'];
                            if (skipTags.includes(tagLower)) return false;
                            
                            // Skip redundant or less important tags
                            const unimportantTags = [
                              'long cook time', 'medium cook time', 'quick',  // Redundant with time display
                              'keto', 'meat', 'protein',                      // Too generic/obvious
                              'cooked', 'hot', 'warm', 'fresh',              // Obvious descriptors
                              'delicious', 'tasty', 'flavorful'              // Generic descriptors
                            ];
                            if (unimportantTags.some(skip => tagLower.includes(skip.toLowerCase()))) return false;
                            
                            // ALWAYS include allergy-related tags (including custom allergies)
                            const isAllergyRelated = (tag: string) => {
                              const tagLowerCheck = tag.toLowerCase();
                              
                              // Standard allergy patterns
                              const allergyPatterns = [
                                'allergen', 'allergy', 'safe', 'free',
                                'dairy-free', 'nut-free', 'soy-free', 'gluten-free', 'egg-free',
                                'milk-free', 'wheat-free', 'peanut-free', 'shellfish-free', 'fish-free'
                              ];
                              
                              // Check if it matches standard allergy patterns
                              if (allergyPatterns.some(pattern => tagLowerCheck.includes(pattern))) {
                                return true;
                              }
                              
                              // Check if it's a custom allergy from user's allergy list
                              const userAllergies = user?.allergies || [];
                              if (userAllergies.some((allergy: string) => {
                                const allergyLower = allergy.toLowerCase();
                                // Match exact allergy or "{allergy}-free" pattern
                                return tagLowerCheck === allergyLower || 
                                       tagLowerCheck === `${allergyLower}-free` ||
                                       tagLowerCheck.includes(allergyLower);
                              })) {
                                return true;
                              }
                              
                              return false;
                            };
                            
                            // Always show allergy-related tags for safety
                            if (isAllergyRelated(tag)) return true;
                            
                            // Keep important contextual tags
                            const importantCategories = [
                              'expiring',                                      // Food waste context
                              'breakfast', 'lunch', 'dinner', 'snack', 'dessert',  // Meal planning
                              'italian', 'asian', 'mexican', 'mediterranean', 'indian', 'american',  // Cuisine
                              'spice', 'mild', 'medium spice', 'spicy',      // Spice preference
                              'inventory', 'mixed',                           // Source context
                              'healthy', 'budget-friendly', 'high protein', 'veggie-packed', 'extra'  // Focus preferences
                            ];
                            
                            return importantCategories.some(category => 
                              tagLower.includes(category.toLowerCase())
                            );
                          });
                          
                          // Render filtered tags with unique color coding
                          return filteredTags.map((tag: string, index: number) => {
                            // Get unique color for each tag type
                            const getTagColor = (tag: string) => {
                              const tagLower = tag.toLowerCase();
                              
                              // Priority-based color assignment with unique colors
                              
                              // Photo Recipe Tags - Special highlighting for photo-generated recipes
                              if (tagLower.includes('photo') && tagLower.includes('recipe')) return 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-700';
                              if (tagLower.includes('detected') && tagLower.includes('ingredients')) return 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-700';
                              if (tagLower.includes('fresh ingredients')) return 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-700';
                              
                              if (tagLower.includes('expiring')) return 'bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-300';
                              
                              // Meal Types
                              if (tagLower === 'lunch') return 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300';
                              if (tagLower === 'breakfast') return 'bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300';
                              if (tagLower === 'dinner') return 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300';
                              if (tagLower === 'snack') return 'bg-pink-50 dark:bg-pink-900/20 text-pink-700 dark:text-pink-300';
                              if (tagLower === 'dessert') return 'bg-fuchsia-50 dark:bg-fuchsia-900/20 text-fuchsia-700 dark:text-fuchsia-300';
                              
                              // Cuisines
                              if (tagLower === 'italian') return 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300';
                              if (tagLower === 'asian') return 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300';
                              if (tagLower === 'mexican') return 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300';
                              if (tagLower === 'mediterranean') return 'bg-cyan-50 dark:bg-cyan-900/20 text-cyan-700 dark:text-cyan-300';
                              if (tagLower === 'indian') return 'bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-300';
                              if (tagLower === 'american') return 'bg-slate-50 dark:bg-slate-900/20 text-slate-700 dark:text-slate-300';
                              
                              // Spice Levels
                              if (tagLower.includes('mild') && tagLower.includes('spice')) return 'bg-lime-50 dark:bg-lime-900/20 text-lime-700 dark:text-lime-300';
                              if (tagLower.includes('medium') && tagLower.includes('spice')) return 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-300';
                              if (tagLower.includes('spicy') || (tagLower.includes('spice') && !tagLower.includes('mild') && !tagLower.includes('medium'))) return 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300';
                              
                              // Source Types
                              if (tagLower.includes('inventory')) return 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300';
                              if (tagLower.includes('mixed')) return 'bg-cyan-50 dark:bg-cyan-900/20 text-cyan-700 dark:text-cyan-300';
                              
                              // Allergen Safety - Enhanced for custom allergies
                              if (tagLower.includes('allergen-safe')) return 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300';
                              if (tagLower.includes('dairy-free')) return 'bg-sky-50 dark:bg-sky-900/20 text-sky-700 dark:text-sky-300';
                              if (tagLower.includes('nut-free')) return 'bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-300';
                              if (tagLower.includes('soy-free')) return 'bg-stone-50 dark:bg-stone-900/20 text-stone-700 dark:text-stone-300';
                              if (tagLower.includes('gluten-free')) return 'bg-zinc-50 dark:bg-zinc-900/20 text-zinc-700 dark:text-zinc-300';
                              if (tagLower.includes('egg-free')) return 'bg-neutral-50 dark:bg-neutral-900/20 text-neutral-700 dark:text-neutral-300';
                              
                              // Custom Allergy Detection - Check if it's a user's custom allergy
                              const userAllergies = user?.allergies || [];
                              const isCustomAllergy = userAllergies.some((allergy: string) => {
                                const allergyLower = allergy.toLowerCase();
                                return tagLower === allergyLower || 
                                       tagLower === `${allergyLower}-free` ||
                                       tagLower.includes(allergyLower);
                              });
                              
                              if (isCustomAllergy) {
                                // Use distinct safety colors for custom allergies
                                const allergyColors = [
                                  'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300',
                                  'bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300',
                                  'bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-300',
                                  'bg-pink-50 dark:bg-pink-900/20 text-pink-700 dark:text-pink-300',
                                  'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300'
                                ];
                                // Use hash of tag name to consistently assign colors
                                const colorIndex = Math.abs(tagLower.split('').reduce((a, b) => a + b.charCodeAt(0), 0)) % allergyColors.length;
                                return allergyColors[colorIndex];
                              }
                              
                              // Focus Preferences
                              if (tagLower.includes('healthy')) return 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300';
                              if (tagLower.includes('budget-friendly')) return 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300';
                              if (tagLower.includes('high protein') || tagLower.includes('extra protein')) return 'bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-300';
                              if (tagLower.includes('veggie-packed') || tagLower.includes('extra vegetables')) return 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300';
                              
                              // Default
                              return 'bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300';
                            };
                            
                            return (
                              <div key={index} className={`flex items-center rounded-md px-2 py-1 text-xs font-medium ${getTagColor(tag)}`}>
                                <span>{tag}</span>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    </div>

                    {/* Enhanced Calorie Progress */}
                    <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Daily Calorie Progress</span>
                        {/* Desktop only: Remaining calories in top right */}
                        {!isMobile && (
                          <span className={`text-sm ${
                            newTotal < dailyGoal 
                              ? 'text-green-600 dark:text-green-400 font-medium' 
                              : 'text-red-600 dark:text-red-400 font-medium'
                          }`}>
                            {newTotal < dailyGoal 
                                              ? `${formatCalories(dailyGoal - newTotal)} calories remaining`
                : `${formatCalories(newTotal - dailyGoal)} calories over goal`
                            }
                          </span>
                        )}
                      </div>
                      
                      {/* Custom dual-color animated progress bar - Layered behind/in-front */}
                      <div className="relative">
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-4 overflow-hidden relative">
                          {/* Current intake (blue) - Top layer, grows first */}
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${Math.min((currentIntake / dailyGoal) * 100, 100)}%` }}
                            transition={{ duration: 1, ease: "easeOut" }}
                            className="h-full bg-gradient-to-r from-blue-400 to-blue-500 rounded-full relative z-10"
                          />
                          
                          {/* Total intake (orange) - Background layer, starts halfway through */}
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${Math.min(((currentIntake + recipe.calories) / dailyGoal) * 100, 100)}%` }}
                            transition={{ duration: 1, delay: 0.5, ease: "easeOut" }}
                            className="h-full bg-gradient-to-r from-orange-400 to-orange-500 rounded-full absolute top-0 left-0"
                          />
                          
                          {/* Shine effect */}
                          <motion.div
                            animate={{ x: [-50, 400] }}
                            transition={{ duration: 2, delay: 2, ease: "easeInOut" }}
                            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent w-12 z-20"
                          />
                        </div>
                      </div>
                      
                      {/* Progress legend and details */}
                      <div className="flex items-center mt-3">
                        <div className="flex items-center space-x-4 text-sm">
                          <div className="flex items-center space-x-2">
                            <div className="w-3 h-3 bg-gradient-to-r from-blue-400 to-blue-500 rounded-full"></div>
                            <span className="text-gray-600 dark:text-gray-400">
                              Current: <span className="font-medium">{currentIntake} cal</span>
                            </span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <div className="w-3 h-3 bg-gradient-to-r from-orange-400 to-orange-500 rounded-full"></div>
                            <span className="text-gray-600 dark:text-gray-400">
                              This meal: <span className="font-medium text-orange-600">+{formatCalories(recipe.calories)} cal</span>
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Remaining calories indicator - Mobile only */}
                      {isMobile && (
                        <div className="mt-2 text-center">
                          {newTotal < dailyGoal ? (
                            <span className="text-sm text-green-600 dark:text-green-400 font-medium">
                              {formatCalories(dailyGoal - newTotal)} calories remaining
                            </span>
                          ) : (
                            <span className="text-sm text-red-600 dark:text-red-400 font-medium">
                              {formatCalories(newTotal - dailyGoal)} calories over goal
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* 🍽️ UNIFIED MOBILE NUTRITION FACTS */}
                    {isMobile && (
                      <div className="mt-8" ref={nutritionSectionRef}>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                          Nutrition Facts
                        </h3>
                        
                        {/* Mobile 2-column grid using unified component */}
                        <div className="grid grid-cols-2 gap-3">
                                                    {getNutritionCardData().map((card, index) => renderNutritionCard(card, index))}
                        </div>
                      </div>
                    )}

                  </div>
                </div>
              </div>

              {/* 🍽️ UNIFIED DESKTOP NUTRITION FACTS */}
              {!isMobile && (
                <div className="px-6 pb-6">
                  <div className="mt-8" ref={nutritionSectionRef}>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                      Nutrition Facts
                    </h3>
                    
                    {/* Desktop 3-column grid using unified component */}
                    <div className="grid grid-cols-3 gap-4">
                                            {getNutritionCardData().map((card, index) => renderNutritionCard(card, index))}
                    </div>
                  </div>
                </div>
              )}

              {/* Meal Personalization Summary */}
              <div className="px-6 pb-6">
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7 }}
                  className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 border border-indigo-200 dark:border-indigo-800/30 rounded-2xl mb-6"
                >
                  {/* Collapsible Header Button */}
                  <button
                    onClick={() => setIsPersonalizationExpanded(!isPersonalizationExpanded)}
                    className="w-full flex items-center justify-between p-6 hover:bg-indigo-100/50 dark:hover:bg-indigo-900/30 transition-colors rounded-2xl"
                  >
                    <div className="flex items-center gap-3">
                      {/* Hide icon on mobile to save space */}
                      {!isMobile && (
                        <div className="w-10 h-10 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-xl flex items-center justify-center">
                          <Zap size={20} className="text-white" />
                        </div>
                      )}
                      <div className="text-left">
                        <h3 className="text-lg font-heading font-semibold text-foreground">
                          📊 {isMobile ? 'Recipe Summary' : 'Meal Personalization Summary'}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {macroData.isPhotoRecipe 
                            ? 'Analysis of your photo-based recipe content' 
                            : 'How this recipe was tailored to your goals and preferences'
                          }
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground font-medium">
                        {isPersonalizationExpanded ? 'Hide Details' : 'Show Details'}
                      </span>
                      <ChevronDown 
                        size={20} 
                        className={`text-muted-foreground transition-transform duration-200 ${
                          isPersonalizationExpanded ? 'rotate-180' : ''
                        }`}
                      />
                    </div>
                  </button>

                  {/* Expandable Content */}
                  <AnimatePresence>
                    {isPersonalizationExpanded && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3, ease: 'easeInOut' }}
                        className="overflow-hidden"
                      >
                        <div className="px-6 pb-6 space-y-4">
                    {/* Diet Preference Adaptation */}
                    <motion.div 
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.8 }}
                      className="bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm rounded-xl p-4"
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-green-100 dark:bg-green-900/40 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-sm">🥗</span>
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-foreground text-base mb-1">
                            {macroData.isPhotoRecipe ? 'Photo Analysis' : `${macroData.dietType} Diet Optimization`}
                          </h4>
                          <p className="text-muted-foreground text-sm leading-relaxed mb-2">
                            {macroData.isPhotoRecipe ? (
                              <>
                                AI detected <span className="font-medium text-blue-600 dark:text-blue-400">
                                  {detectedIngredients?.length || 0} ingredients
                                </span> in your photo and created a complete recipe with cooking instructions. 
                                The recipe combines these photo ingredients into a cohesive dish with proper cooking techniques and flavor balancing. 
                                Perfect for recreating what you captured or using ingredients you have on hand.
                              </>
                            ) : (
                              <>
                                Designed for your lifestyle. 
                                {macroData.dietType === 'Keto' && (
                                  <>This recipe keeps carbs ultra-low ({formatMacronutrient(recipe.nutrition?.carbs || 0)}g) while prioritizing healthy fats to maintain ketosis. </>
                                )}
                                {macroData.dietType === 'Low-Carb' && (
                                  <>Moderate carbs ({formatMacronutrient(recipe.nutrition?.carbs || 0)}g) with increased protein to support your low-carb goals. </>
                                )}
                                {macroData.dietType === 'High-Protein' && (
                                  <>High protein content ({formatMacronutrient(recipe.nutrition?.protein || 0)}g) to support muscle building and recovery. </>
                                )}
                                {macroData.dietType === 'Balanced' && (
                                  <>Balanced macronutrient profile that aligns with your dietary preferences and health goals. </>
                                )}
                                Optimized for <span className="font-medium">{macroData.goalOptimized}</span>.
                              </>
                            )}
                          </p>
                          <div className="flex gap-2 flex-wrap">
                            <div className="inline-flex items-center gap-1.5 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-3 py-1 rounded-lg text-xs font-medium">
                              <span>{macroData.isPhotoRecipe ? '📸' : '✓'}</span>
                              <span>{macroData.isPhotoRecipe ? `AI photo detection` : `${macroData.dietType} diet aligned`}</span>
                            </div>
                            {macroData.isPhotoRecipe ? (
                              <div className="inline-flex items-center gap-1.5 bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-3 py-1 rounded-lg text-xs font-medium">
                                <span>🧠</span>
                                <span>Smart recipe creation</span>
                              </div>
                            ) : (
                              <div className="inline-flex items-center gap-1.5 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-3 py-1 rounded-lg text-xs font-medium">
                                <span>🎯</span>
                                <span>{macroData.goalOptimized}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </motion.div>

                                        {/* Allergy Safety & Adaptations */}
                    {allergyData.hasAllergies && (
                      <motion.div 
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.83 }}
                        className="bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm rounded-xl p-4"
                      >
                        <div className="flex items-start gap-3">
                          <div className={`w-8 h-8 ${allergyData.hasViolations ? 'bg-red-100 dark:bg-red-900/40' : 'bg-green-100 dark:bg-green-900/40'} rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5`}>
                            <span className="text-sm">{allergyData.hasViolations ? '⚠️' : '🛡️'}</span>
                          </div>
                          <div className="flex-1">
                            <h4 className="font-semibold text-foreground text-base mb-1">
                              {allergyData.hasViolations ? 'Allergy Safety Warning' : 'Allergy-Safe Adaptations'}
                            </h4>
                            
                            {allergyData.hasViolations ? (
                              <div className="space-y-2">
                                <p className="text-red-600 dark:text-red-400 text-sm leading-relaxed mb-2 font-medium">
                                  ⚠️ WARNING: This recipe contains ingredients that may trigger your allergies!
                                </p>
                                <div className="space-y-1">
                                  {allergyData.violations.map((violation, index) => (
                                    <div key={index} className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-2">
                                      <p className="text-red-700 dark:text-red-300 text-xs font-medium">
                                        <span className="font-semibold">{violation.ingredient}</span> - {violation.reason}
                                      </p>
                                    </div>
                                  ))}
                                </div>
                                <p className="text-muted-foreground text-xs">
                                  Please review all ingredients carefully or request a new recipe that avoids: {allergyData.allergies?.join(', ')}.
                                </p>
                              </div>
                            ) : (
                              <div>
                                <p className="text-muted-foreground text-sm leading-relaxed mb-2">
                                  This recipe completely avoids your allergens: <span className="font-medium text-red-600 dark:text-red-400">
                                    {allergyData.allergies?.join(', ')}
                                  </span>. 
                                  {allergyData.adaptations.length > 0 && (
                                    <>We used safe alternatives like <span className="font-medium">
                                      {allergyData.adaptations.map(a => a.substitutes.map((s: any) => s.name)).flat().slice(0, 2).join(' and ')}
                                    </span> to maintain flavor without compromising your safety. </>
                                  )}
                                  Every ingredient was carefully verified for hidden allergens.
                                </p>
                              </div>
                            )}
                            
                            <div className="flex gap-2 flex-wrap">
                              {allergyData.hasViolations ? (
                                <div className="inline-flex items-center gap-1.5 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 px-3 py-1 rounded-lg text-xs font-medium">
                                  <span>⚠️</span>
                                  <span>Contains allergens - unsafe</span>
                                </div>
                              ) : (
                                <div className="inline-flex items-center gap-1.5 bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-3 py-1 rounded-lg text-xs font-medium">
                                  <span>🛡️</span>
                                  <span>100% allergy-safe</span>
                                </div>
                              )}
                              {allergyData.adaptations.length > 0 && !allergyData.hasViolations && (
                                <div className="inline-flex items-center gap-1.5 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-3 py-1 rounded-lg text-xs font-medium">
                                  <span>↔️</span>
                                  <span>Smart substitutions used</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}

                    {/* Body Stats & Calorie Optimization */}
                    {bodyStatsData.hasBodyStats && (
                      <motion.div 
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.85 }}
                        className="bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm rounded-xl p-4"
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/40 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                            <span className="text-sm">📏</span>
                          </div>
                          <div className="flex-1">
                            <h4 className="font-semibold text-foreground text-base mb-1">
                              Personal Body Stats Optimization
                            </h4>
                            <p className="text-muted-foreground text-sm leading-relaxed mb-2">
                              Based on your stats ({bodyStatsData.heightWeight}), your BMR is {bodyStatsData.bmr} calories and daily needs are {bodyStatsData.tdee} calories. 
                              This meal provides <span className="font-medium text-blue-600 dark:text-blue-400">{formatCalories(recipe.calories)} calories</span> 
                              {bodyStatsData.isOptimized ? ' - perfectly sized for one meal' : ` - close to your ${bodyStatsData.mealTarget} calorie meal target`}. 
                              Portion sizes are calibrated to your individual metabolic needs.
                            </p>
                            <div className="flex gap-2 flex-wrap">
                              <div className="inline-flex items-center gap-1.5 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-3 py-1 rounded-lg text-xs font-medium">
                                <span>📏</span>
                                <span>Personalized portions</span>
                              </div>
                              <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-medium ${
                                bodyStatsData.isOptimized 
                                  ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                                  : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                              }`}>
                                <span>🎯</span>
                                <span>{bodyStatsData.isOptimized ? 'Perfect calorie fit' : 'Close to target'}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}

                    {/* Macro Goals & Protein Targeting */}
                    <motion.div 
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.87 }}
                      className="bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm rounded-xl p-4"
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900/40 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-sm">💪</span>
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-foreground text-base mb-1">
                            Macro Goals Alignment
                          </h4>
                          <p className="text-muted-foreground text-sm leading-relaxed mb-2">
                            {proteinData.ingredients.length > 0 && (
                              <>Protein-rich ingredients like <span className="font-medium text-purple-600 dark:text-purple-400">
                                {proteinData.ingredients.map((ing: any) => ing.name).join(' and ')}
                              </span> deliver <span className="font-semibold">{proteinData.amount}g protein</span>. </>
                            )}
                            Macros perfectly balanced for {macroData.dietType.toLowerCase()} eating: 
                            <span className={`font-medium ${macroData.alignment.protein ? 'text-green-600 dark:text-green-400' : 'text-orange-600 dark:text-orange-400'}`}>
                              {formatMacronutrient(recipe.nutrition?.protein || 0)}g protein
                            </span>, 
                            <span className={`font-medium ${macroData.alignment.carbs ? 'text-green-600 dark:text-green-400' : 'text-orange-600 dark:text-orange-400'}`}>
                              {formatMacronutrient(recipe.nutrition?.carbs || 0)}g carbs
                            </span>, 
                            <span className={`font-medium ${macroData.alignment.fat ? 'text-green-600 dark:text-green-400' : 'text-orange-600 dark:text-orange-400'}`}>
                              {formatMacronutrient(recipe.nutrition?.fat || 0)}g fat
                            </span>.
                          </p>
                          <div className="flex gap-2 flex-wrap">
                            <div className="inline-flex items-center gap-1.5 bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 px-3 py-1 rounded-lg text-xs font-medium">
                              <span>💪</span>
                              <span>{proteinData.amount}g protein boost</span>
                            </div>
                            <div className="inline-flex items-center gap-1.5 bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-3 py-1 rounded-lg text-xs font-medium">
                              <span>⚖️</span>
                              <span>{macroData.dietType} macro balance</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </motion.div>

                    {/* Smart Inventory Use - Hidden for photo recipes */}
                    {!macroData.isPhotoRecipe && (
                      <motion.div 
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.9 }}
                        className="bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm rounded-xl p-4"
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 bg-orange-100 dark:bg-orange-900/40 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                            <span className="text-sm">📦</span>
                          </div>
                          <div className="flex-1">
                            <h4 className="font-semibold text-foreground text-base mb-1">
                              Smart Inventory Integration
                            </h4>
                            <p className="text-muted-foreground text-sm leading-relaxed mb-2">
                              Maximizes your existing pantry with <span className="font-medium text-orange-600 dark:text-orange-400">
                                {inventoryData.count} ingredient{inventoryData.count !== 1 ? 's' : ''} you already have
                              </span>
                              {inventoryData.expiring.length > 0 && (
                                <>, prioritizing your <span className="font-medium">
                                  {inventoryData.expiring[0].name} that expires soon
                                </span> to reduce waste</>
                              )}. 
                              AI analyzed your full inventory to create this personalized recipe while {inventoryData.expiring.length > 0 ? 'preventing food waste' : 'minimizing shopping needs'}.
                            </p>
                            <div className="flex gap-2 flex-wrap">
                              {inventoryData.expiring.length > 0 && (
                                <div className="inline-flex items-center gap-1.5 bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 px-3 py-1 rounded-lg text-xs font-medium">
                                  <span>♻️</span>
                                  <span>Prevents food waste</span>
                                </div>
                              )}
                              <div className="inline-flex items-center gap-1.5 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-3 py-1 rounded-lg text-xs font-medium">
                                <span>📦</span>
                                <span>{inventoryData.count}/{inventoryData.total} from your pantry</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}

                    {/* Time & Complexity Adaptation */}
                    <motion.div 
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.92 }}
                      className="bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm rounded-xl p-4"
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-indigo-100 dark:bg-indigo-900/40 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-sm">⏱️</span>
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-foreground text-base mb-1">
                            Time & Lifestyle Optimization
                          </h4>
                          <p className="text-muted-foreground text-sm leading-relaxed mb-2">
                            {recipe.difficulty === 'Easy' ? 'Simplified for busy schedules with ' : 'Balanced complexity offering '}
                            <span className="font-medium text-indigo-600 dark:text-indigo-400">{recipe.cookTime}-minute cooking time</span>. 
                            {recipe.difficulty === 'Easy' 
                              ? 'Minimal prep work and straightforward steps reduce kitchen stress while delivering maximum flavor.'
                              : 'Engaging cooking process that rewards your effort with complex flavors and satisfying results.'
                            }
                          </p>
                          <div className="flex gap-2 flex-wrap">
                            <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-medium ${
                              recipe.difficulty === 'Easy' 
                                ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                                : 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300'
                            }`}>
                              <span>⏱️</span>
                              <span>{recipe.difficulty} {recipe.cookTime}-min recipe</span>
                            </div>
                            <div className="inline-flex items-center gap-1.5 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-3 py-1 rounded-lg text-xs font-medium">
                              <span>👨‍🍳</span>
                              <span>Lifestyle-optimized</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </motion.div>


                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              </div>

              {/* Ingredients & Instructions */}
              <div className="px-6 pb-6">
                {/* Portion Selector */}
                <div className="mb-6 flex items-center justify-center">
                  <div className="inline-flex items-center gap-3 bg-gray-50 dark:bg-gray-800 rounded-xl px-4 py-3">
                    <span className="text-sm font-medium text-muted-foreground">Portions:</span>
                    <button
                      onClick={() => setSelectedPortions(Math.max(1, selectedPortions - 1))}
                      className="w-8 h-8 rounded-lg bg-white dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors flex items-center justify-center disabled:opacity-50"
                      disabled={selectedPortions <= 1}
                    >
                      <span className="text-base font-medium text-gray-600 dark:text-gray-300">−</span>
                    </button>
                    <span className="min-w-[1.5rem] text-center font-semibold text-foreground">{selectedPortions}</span>
                    <button
                      onClick={() => setSelectedPortions(Math.min(10, selectedPortions + 1))}
                      className="w-8 h-8 rounded-lg bg-white dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors flex items-center justify-center disabled:opacity-50"
                      disabled={selectedPortions >= 10}
                    >
                      <span className="text-base font-medium text-gray-600 dark:text-gray-300">+</span>
                    </button>
                    <span className="text-sm text-muted-foreground">
                      {selectedPortions === 1 ? 'person' : 'people'}
                    </span>
                  </div>
                </div>

                <div className="grid lg:grid-cols-2 gap-6">
                  {/* Ingredients */}
                  <div className={`space-y-4 ${isMobile ? 'max-w-full overflow-hidden' : ''}`}>
                    <h3 className="text-lg font-heading font-semibold text-foreground">Ingredients</h3>
                    <div className="space-y-2">
                      {recipe.ingredients.map((ingredient: any, index: number) => {
                        const locationInfo = getIngredientLocation(ingredient.name);
                        return (
                          <div key={index} className="flex items-center justify-between gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <div className="w-2 h-2 bg-primary rounded-full flex-shrink-0" />
                              <span className="font-medium text-sm">
                                {scaleIngredientAmount(ingredient.amount, selectedPortions)}
                              </span>
                              <span className="text-sm text-muted-foreground truncate">{ingredient.name}</span>
                            </div>
                            <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-medium flex-shrink-0 ${locationInfo.bgColor} ${locationInfo.textColor}`}>
                              <span>{locationInfo.emoji}</span>
                              <span>{locationInfo.location}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Instructions */}
                  <div className={`space-y-4 ${isMobile ? 'max-w-full overflow-hidden' : ''}`}>
                    <h3 className="text-lg font-heading font-semibold text-foreground">Instructions</h3>
                    <div className="space-y-3">
                      {recipe.instructions.map((instruction: any, index: number) => (
                        <div key={index} className="flex gap-4">
                          <div className="w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">
                            {instruction.step}
                          </div>
                          <p className="text-sm text-muted-foreground leading-relaxed pt-1">
                            {addEmojisToInstruction(instruction.instruction, inventory, isPhotoRecipe, detectedIngredients)}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Safety Disclaimer */}
                <div className={`mx-6 mt-8 ${isMobile ? 'mb-12' : 'mb-6'}`}>
                  <div className="bg-gradient-to-r from-amber-50/50 to-yellow-50/50 dark:from-amber-900/10 dark:to-yellow-900/10 border border-dashed border-amber-200/60 dark:border-amber-700/30 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-0.5">
                        <span className="text-amber-500/80 dark:text-amber-500/60 text-lg">⚠️</span>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-amber-700/90 dark:text-amber-300/80 text-sm mb-1">
                          Safety Reminder
                        </h4>
                        <p className="text-amber-600/80 dark:text-amber-400/70 text-xs leading-relaxed">
                          AI-generated recipes are suggestions only. Please carefully review all ingredients to ensure they are safe for your dietary restrictions and allergies. 
                          Cross-check each ingredient against your known allergens before cooking. When in doubt, consult with a healthcare professional or substitute with safer alternatives.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer Actions */}
            <div className={`${
              isMobile 
                ? 'absolute bottom-0 left-0 right-0 bg-white dark:bg-slate-800 border-t border-gray-200 dark:border-slate-700 p-4 z-50' 
                : 'sticky bottom-0 bg-white dark:bg-slate-800 border-t border-gray-200 dark:border-slate-700 p-6 rounded-b-2xl'
            }`}
            style={isMobile ? {
              paddingBottom: isPWAStandalone() ? '1rem' : 'env(safe-area-inset-bottom, 0.5rem)', // Respect safe area for PWA, minimal padding for browsers
              minHeight: '80px' // Reduced minimum height
            } : undefined}
            >
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={handleSaveRecipe}
                  className={`flex-1 ${
                    isMobile 
                      ? 'h-12 rounded-2xl text-base font-medium' 
                      : ''
                  }`}
                  disabled={isSaved}
                >
                  {isSaved ? (
                    <>
                      <Check size={16} className="mr-2" />
                      Saved
                    </>
                  ) : (
                    <>
                      <Heart size={16} className="mr-2" />
                      Save to Favorites
                    </>
                  )}
                </Button>
                <Button
                  onClick={handleLogMeal}
                  className={`flex-1 bg-primary hover:bg-primary/90 ${
                    isMobile 
                      ? 'h-12 rounded-2xl text-base font-medium shadow-lg' 
                      : ''
                  }`}
                >
                  <Plus size={16} className="mr-2" />
                  Log as Meal
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );

  // Confirmation Modal for Delete
  const confirmationModal = showDeleteConfirm && (
    <div className="fixed inset-0 flex items-center justify-center z-[10000]" style={{ backdropFilter: 'blur(8px)' }}>
      <div className="absolute inset-0 bg-black/60" onClick={() => setShowDeleteConfirm(false)} />
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.2 }}
        className="relative bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-gray-200 dark:border-slate-700 p-6 mx-4 max-w-md w-full"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-red-100 dark:bg-red-900/40 rounded-full flex items-center justify-center">
            <Trash2 size={20} className="text-red-600 dark:text-red-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Delete Recipe
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              This action cannot be undone
            </p>
          </div>
        </div>
        
        <p className="text-gray-600 dark:text-gray-300 mb-6">
          Are you sure you want to delete "<strong>{recipe?.name}</strong>"? This will permanently remove the recipe and all its data from your collection.
        </p>
        
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={() => setShowDeleteConfirm(false)}
            className="flex-1"
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteRecipe}
            className="flex-1 bg-red-600 hover:bg-red-700 text-white"
            disabled={isDeleting}
          >
            {isDeleting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                Deleting...
              </>
            ) : (
              <>
                <Trash2 size={16} className="mr-2" />
                Delete Recipe
              </>
            )}
          </Button>
        </div>
      </motion.div>
    </div>
  );

  return ReactDOM.createPortal(
    <>
      {modal}
      {confirmationModal}
    </>,
    document.body
  );
} 